// ── INVOICES TAB ──────────────────────────────────────────────────────────────
// Single source of truth for every invoice the firm generates: deposit,
// final, reimbursable, designer hours.
//
// List view: filter chips by type and status, flat list with per-row pill.
// Click a row → modal with PDF preview (regenerated on demand from the saved
// line_items snapshot) plus actions: Download, Mark Sent, Mark Paid, Delete.
//
// Invoices originate in:
//   - Proposal detail → Deposit Invoice button (this builds the line_items
//     snapshot and persists it; PDF can be re-rendered any time)
//   - Billing tab Invoicing sub-tab → Final invoice on received items
//     (currently saves a shallow row; the row appears here but cannot
//     re-render a PDF until the BillingTab path is wired to the generator)
//   - Future: reimbursable monthly rollup, designer-hours monthly invoice

import React, { useState, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { useDetailJump } from '../lib/jump';
import { useAppContext } from './AppContext';
import {
  isItemInProcess,
  isItemReadyForFinalInvoice,
  estimatedFinalInvoiceAmount,
  groupItemsByPO,
  buildFinalInvoicePayload,
  isBillable,
  calcPaidForDocument,
  derivePaidStatus,
  finPoSummary,
  finReceiptStatus,
  paymentEntriesForDocument,
  calcBillingSummaryAmount,
  computeCascadeTargets,
  computePartialCascadeTargets,
  finDepositPaymentHistory,
  fmtMoney,
  hasAdminAccess,
  itemPaidShare,
  itemLifecycleOwed,
  isPoFullyPaid,
  poHasVendorPrincipalPayment,
  computePoItemPaymentHistory,
  itemNet,
  applyFreightMarkup, customFeeClient,
  applyTaxMarkup,
  validateAddItemToFinSelection,
  invoiceCrossRefs,
  buildInvoiceRegenPayload,
  canRefreshInvoiceSnapshot,
  refreshInvoiceSnapshotSpecs,
  billingSummaryMonthYear,
  ccFeeSources,
  hasProjectTaxConfig,
} from '../utils';
import DesignerHoursSection from './DesignerHoursSection';
import BillingSummarySection from './BillingSummarySection';
import RetainersSection from './RetainersSection';
import ClientCreditsSection from './ClientCreditsSection';
import BillingHistorySection from './BillingHistorySection';
import ChangeOrdersTab from './ChangeOrdersTab';
import ReceiptsTab from './ReceiptsTab';
import PaymentsPanel from './PaymentsPanel';
import PaymentsLedgerSection from './PaymentsLedgerSection';
import BankDepositModal from './BankDepositModal';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

// Designer-hours invoices live in their own table (hours_invoices) with their
// own Toggl-driven workflow under Billing → Hourly & Expenses. They are not
// surfaced in this tab. The mapping is kept here only so legacy rows of that
// type (if any exist in the invoices table) still display a friendly label.
var TYPE_LABEL = {
  deposit:        'Deposit',
  final:          'Final',
  reimbursable:   'Reimbursable',
  designer_hours: 'Designer Hours',
};

// Fix D Phase 1.3 (2026-05-31) — rowToInvoice normalizes status to lowercase,
// so the legacy Design Manager casings ('Paid' / 'Unpaid') reach this map as
// 'paid' / 'unpaid'. 'unpaid' is treated as 'sent' semantically (DM imports
// that haven't been paid yet).
var STATUS_STYLE = {
  draft:   { dot: '#F5C800', text: '#555552', label: 'Draft' },
  // 'ready' state for FINs that have been Mark-Ready'd (2026-06-07
  // scaffold, cascade wired 2026-06-26). Green dot to distinguish
  // from yellow Draft and fuchsia Sent.
  ready:   { dot: '#1A5C38', text: '#555552', label: 'Ready' },
  sent:    { dot: '#D4007A', text: '#A8005A', label: 'Sent' },
  unpaid:  { dot: '#F5C800', text: '#555552', label: 'Unpaid' },
  partial: { dot: '#D4007A', text: '#A8005A', label: 'Partially Paid' },
  paid:    { dot: '#1A5C38', text: '#555552', label: 'Paid' },
  void:    { dot: '#888880', text: '#888880', label: 'Void' },
};


// Designer hours invoices have their own dedicated workflow in BillingTab →
// Hourly & Expenses (Toggl pull → Claude summary → generate-hours-invoice.py),
// stored in the separate hours_invoices table. They are intentionally NOT
// surfaced here.
// Build a short human-readable description of what's on an invoice. Used in
// the list view "For" column so the user doesn't have to guess what each
// invoice is for. Strategy:
//   1 line item  → its product name
//   2-3 line items → comma-joined names
//   4+ line items → "first item + N more"
//   no line items (legacy rows) → fall back to invoice notes / type label
function invoiceDescription(inv) {
  var lines = inv.lineItems || inv.line_items || [];
  if (Array.isArray(lines) && lines.length > 0) {
    var names = lines
      .map(function(li) { return li.productName || li.item || li.description || ''; })
      .filter(function(n) { return n; });
    if (names.length === 1) return names[0];
    if (names.length <= 3) return names.join(', ');
    return names[0] + ' + ' + (names.length - 1) + ' more';
  }
  // Reimbursable invoices may not list product names — fall back to notes
  // or a generic description.
  if (inv.notes) return String(inv.notes).split('\n')[0].slice(0, 80);
  return '\u2014';
}

// ── FORMATTING ────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return String(iso).slice(0, 10); }
}

// ── STYLES ────────────────────────────────────────────────────────────────────

var S = {
  wrap:        { fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: '#111111', background: '#FFFFFF', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  toolbar:     { display: 'flex', alignItems: 'center', height: 44, padding: '0 14px', gap: 8, borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5', flexShrink: 0, flexWrap: 'wrap' },
  pageTitle:   { fontSize: 13, fontWeight: 500, color: '#111111', whiteSpace: 'nowrap' },
  vsep:        { width: '0.5px', height: 18, background: '#D8D7D2', margin: '0 2px', flexShrink: 0 },
  tbtnGhost:   { height: 28, padding: '0 10px', borderRadius: 5, border: '0.5px solid #D8D7D2', background: '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#111111', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnPrimary: { height: 28, padding: '0 11px', borderRadius: 5, border: '0.5px solid #111111', background: '#111111', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#FFFFFF', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnDanger:  { height: 28, padding: '0 10px', borderRadius: 5, border: '0.5px solid #C8322A', background: '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#C8322A', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnSuccess: { height: 28, padding: '0 11px', borderRadius: 5, border: '0.5px solid #1A5C38', background: '#1A5C38', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#FFFFFF', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  list:        { flex: 1, overflowY: 'auto', padding: '0' },
  // position:sticky keeps the header row pinned while the list scrolls
  // (Cimon 2026-08-26: "always use column headers that stick"). The
  // boxShadow draws the bottom hairline because border-collapse borders
  // scroll away from stuck cells; background must stay opaque.
  th:          { padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', whiteSpace: 'nowrap', borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5', position: 'sticky', top: 0, zIndex: 2, boxShadow: '0 0.5px 0 #D8D7D2' },
  // overflow/ellipsis matter under tableLayout:'fixed' — without them a
  // long nowrap value bleeds into the neighbor column instead of
  // truncating (the PO-Vendor/Client gap complaint, 2026-08-26).
  td:          { padding: '10px 14px', color: '#555552', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
};

// ── STATUS PILL ───────────────────────────────────────────────────────────────

function StatusPill(p) {
  var s = STATUS_STYLE[p.status] || STATUS_STYLE.draft;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      <span style={{ fontSize: 11, color: s.text, fontWeight: 400 }}>{s.label}</span>
    </span>
  );
}

// ── INVOICE TABLE ─────────────────────────────────────────────────────────────
// Shared row renderer for the Deposit + draft/ready-Final lists. Rebuilt
// 2026-07-19 for Dan's QB reconciliation (Cimon: "add Proposal number and
// billing summary month... make columns sortable... include sku number
// under For"): Client + Project columns dropped (the toolbar already
// narrows by project), Proposal + Billing Month added, every column
// header click-sorts, and the For cell carries the SKUs as a subline.
// Cross-references derive from invoiceCrossRefs — the same helper the
// PDF reference block and the detail panel use.

var SORTABLE_COLS = [
  { key: 'ref',      label: 'Ref' },
  { key: 'type',     label: 'Type' },
  { key: 'for',      label: 'For' },
  { key: 'po',       label: 'PO #' },
  { key: 'vendor',   label: 'PO Vendor' },
  { key: 'proposal', label: 'Proposal' },
  { key: 'summary',  label: 'Billing Month' },
  { key: 'amount',   label: 'Amount', right: true },
  { key: 'status',   label: 'Status' },
  { key: 'date',     label: 'Date' },
];

// Shared cell base for InvoiceTable rows — the ellipsis pieces matter
// under tableLayout:'fixed' (long values truncate instead of bleeding).
var CELL = { padding: '10px 14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

function invoiceSortVal(row, key) {
  var inv = row.inv;
  switch (key) {
    case 'ref':      return String(inv.invoiceRef || inv.invoice_ref || '');
    case 'type':     return String(inv.invoiceType || '');
    case 'for':      return String(row.desc || '');
    case 'po':       return row.poSum.poRefs.join(',');
    case 'vendor':   return row.poSum.vendorNames.join(',');
    case 'proposal': return row.xr.proposalRef;
    case 'summary':  return row.xr.summarySortKey;
    case 'amount':   return parseFloat(inv.amountDue || inv.amount_due || inv.amount) || 0;
    case 'status':   return String(inv.status || '');
    case 'date':     return String(inv.createdAt || inv.created_at || '');
    default:         return '';
  }
}

function InvoiceTable(p) {
  var ss = React.useState({ key: 'date', dir: 'desc' });
  var sort = ss[0]; var setSort = ss[1];
  var refsBag = {
    items: p.items || [], proposals: p.proposals || [],
    purchaseOrders: p.purchaseOrders || [], invoices: p.invoices || [],
    billingSummaries: p.billingSummaries || [],
  };
  var rows = (p.rows || []).map(function(inv) {
    return {
      inv: inv,
      xr: invoiceCrossRefs(inv, refsBag),
      poSum: finPoSummary(inv, p.items || [], p.purchaseOrders || []),
      desc: invoiceDescription(inv),
    };
  });
  rows.sort(function(a, b) {
    var av = invoiceSortVal(a, sort.key), bv = invoiceSortVal(b, sort.key);
    // Empty values sink to the bottom in either direction.
    var ae = (av === '' || av === 0), be = (bv === '' || bv === 0);
    if (ae !== be) return ae ? 1 : -1;
    var c = (typeof av === 'number' && typeof bv === 'number')
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sort.dir === 'asc' ? c : -c;
  });
  function clickSort(key) {
    setSort(function(prev) {
      return { key: key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }
  // hiddenCols: column keys the calling view suppresses. Drafts/Ready pass
  // ['summary'] — Billing Month only fills when a FIN rides a billing
  // summary to the client, so pre-send views rendered a permanently empty
  // column (user-reported 2026-08-26).
  var hidden = p.hiddenCols || [];
  var cols = SORTABLE_COLS.filter(function(c) { return hidden.indexOf(c.key) < 0; });
  // tableLayout:'fixed': compact columns hold their width, For takes the
  // remainder — auto layout spread the slack as dead space between columns.
  var COL_WIDTHS = { ref: 132, type: 62, for: null, po: 118, vendor: 148, proposal: 112, summary: 100, amount: 92, status: 80, date: 94 };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' }}>
      <colgroup>
        {cols.map(function(col) {
          var w = COL_WIDTHS[col.key];
          return <col key={col.key} style={w ? { width: w } : {}} />;
        })}
      </colgroup>
      <thead>
        <tr>
          {cols.map(function(col) {
            var active = sort.key === col.key;
            return (
              <th key={col.key}
                data-testid={'invcol-' + col.key}
                onClick={function() { clickSort(col.key); }}
                style={Object.assign({}, S.th, { cursor: 'pointer', userSelect: 'none' },
                  col.right ? { textAlign: 'right' } : {},
                  active ? { color: '#111111' } : {})}>
                {col.label}{active ? (sort.dir === 'asc' ? ' \u2191' : ' \u2193') : ''}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map(function(row) {
          var inv = row.inv;
          var ref     = inv.invoiceRef    || inv.invoice_ref    || inv.invoice_number || '\u2014';
          var type    = inv.invoiceType   || 'final';
          // Class C migrated invoices populate the legacy `amount` column;
          // amountDue is zero. Fallback chain renders the original total
          // for those rows.
          var amount  = inv.amountDue     || inv.amount_due     || inv.amount || 0;
          var status  = inv.status        || 'draft';
          var date    = inv.createdAt     || inv.created_at     || '';
          var poRefDisplay    = row.poSum.poRefs.length      ? row.poSum.poRefs.join(', ')      : '\u2014';
          var poVendorDisplay = row.poSum.vendorNames.length ? row.poSum.vendorNames.join(', ') : '\u2014';
          return (
            <tr key={inv.id}
              onClick={function() { p.onRowClick(inv); }}
              style={{ cursor: 'pointer', background: '#FFFFFF', borderBottom: '0.5px solid #EFEFEC' }}
              onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
              onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
              <td style={Object.assign({}, CELL, { fontFamily: 'monospace', fontSize: 11, color: '#111111', fontWeight: 500 })}>{ref}</td>
              <td style={Object.assign({}, CELL, { color: '#555552' })}>{TYPE_LABEL[type] || type}</td>
              <td style={Object.assign({}, CELL, { color: '#555552' })} title={row.desc}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.desc}</div>
                {row.xr.skus.length > 0 && (
                  <div data-testid="invrow-skus" style={{ fontFamily: 'monospace', fontSize: 9, color: '#888880', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                       title={row.xr.skus.join(', ')}>
                    {row.xr.skus.join(' \u00b7 ')}
                  </div>
                )}
              </td>
              <td style={Object.assign({}, CELL, { fontFamily: 'monospace', color: row.poSum.poRefs.length ? '#111111' : '#888880' })} title={poRefDisplay}>{poRefDisplay}</td>
              <td style={Object.assign({}, CELL, { color: row.poSum.vendorNames.length ? '#111111' : '#888880' })} title={poVendorDisplay}>{poVendorDisplay}</td>
              <td data-testid="invrow-proposal" style={Object.assign({}, CELL, { fontFamily: 'monospace', color: row.xr.proposalRef ? '#111111' : '#888880' })}>{row.xr.proposalRef || '\u2014'}</td>
              {hidden.indexOf('summary') < 0 && (
                <td data-testid="invrow-summary" style={Object.assign({}, CELL, { color: row.xr.summaryMonthYear ? '#555552' : '#888880' })}>{row.xr.summaryMonthYear || '\u2014'}</td>
              )}
              <td style={Object.assign({}, CELL, { textAlign: 'right', fontWeight: 500, color: '#111111' })}>{fmtMoney(amount)}</td>
              <td style={CELL}><StatusPill status={status} /></td>
              <td style={Object.assign({}, CELL, { color: '#888880' })}>{fmtDate(date)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function InvoicesTab() {
  var ctx = useAppContext();
  var invoices = ctx.invoices || [];
  var allItems = ctx.items || [];
  var purchaseOrders = ctx.purchaseOrders || [];
  var allProjects = ctx.projects || [];
  var allClients = ctx.clients || [];
  var allProposals = ctx.proposals || [];
  var firmSettings = ctx.firmSettings || {};
  var saveInvoice = ctx.saveInvoice;
  var deleteInvoice = ctx.deleteInvoice;
  var updateItem = ctx.updateItem;
  var userEmail = (ctx.userEmail || '').toLowerCase();
  var isOwner = ctx.isOwner;
  var clientPayments = ctx.clientPayments || [];
  var paymentAllocations = ctx.paymentAllocations || [];
  var recordClientPayment = ctx.recordClientPayment;
  var updateClientPayment = ctx.updateClientPayment;
  var removePaymentAllocation = ctx.removePaymentAllocation;
  var changeOrders = ctx.changeOrders || [];
  // Billing-summary periods for the list's Billing Month column + the
  // detail panel cross-refs (2026-07-19). Light columns only; loaded
  // page-scoped like BillingHistorySection does.
  var bss = useState([]); var billingSummaries = bss[0]; var setBillingSummaries = bss[1];
  React.useEffect(function() {
    var alive = true;
    supabase.from('billing_summaries')
      .select('id, month, year, summary_type, sent_at, generated_at')
      .then(function(r) {
        if (alive && !r.error) setBillingSummaries(r.data || []);
      });
    return function() { alive = false; };
  }, []);
  // markChangeOrderBilledStandalone is intentionally NOT pulled here. Final
  // invoice generation goes through finalizeInvoiceAtomic so the invoice +
  // item stamps + CO stamps roll back together if any write fails. See
  // LIFECYCLES.md Appendix C #17 and __tests__/finalInvoiceAtomic.test.js.
  var finalizeInvoiceAtomic = ctx.finalizeInvoiceAtomic;

  // Sub-tab navigation. The "Invoices" sub-tab (slug 'all') is the unified
  // surface for both deposit + final invoices, organized as two sections:
  //   - DEPOSIT INVOICES — list of deposit invoices (auto-created at Client
  //     Approved; no picker since the lifecycle is automatic).
  //   - FINAL INVOICES — three sub-views via pill nav:
  //       open    : items eligible to be billed on a final invoice (the
  //                 former "Orders in Process" picker; visual redesign
  //                 deferred to a follow-up commit).
  //       drafts  : final invoices saved as drafts (capability deferred).
  //       sent    : final invoices committed and sent (status='sent').
  // The separate 'orders' sub-tab was retired here — its functionality moved
  // into FINAL INVOICES → open.
  var sts = useState('all');             var subTab = sts[0];          var setSubTab = sts[1];
  var fvs = useState('open');            var finalSubView = fvs[0];    var setFinalSubView = fvs[1];
  var bds = useState(false);             var depositModalOpen = bds[0]; var setDepositModalOpen = bds[1];
  var canManagePayments = ctx.isOwner || hasAdminAccess(ctx.userEmail, ctx.firmSettings);

  // Orders in Process state
  var ofs = useState('');                var oipProjFilter = ofs[0];   var setOipProjFilter = ofs[1];
  var sfis = useState(new Set());        var selectedFinalIds = sfis[0]; var setSelectedFinalIds = sfis[1];
  var fpus = useState(null);             var finalPdfUrl = fpus[0];    var setFinalPdfUrl = fpus[1];
  var fpfs = useState('');               var finalPdfFilename = fpfs[0]; var setFinalPdfFilename = fpfs[1];
  var fls = useState(false);             var finalLoading = fls[0];    var setFinalLoading = fls[1];
  var fes = useState('');                var finalErr = fes[0];        var setFinalErr = fes[1];
  // Pending final-invoice draft. Generate stashes the in-memory invoice
  // record + itemIds + coIds here while the PDF preview is open. Save &
  // Commit reads from this state and calls finalizeInvoiceAtomic. Cancel
  // clears it without any DB write. Keeps the commit decision in the
  // bookkeeper's hands instead of firing on PDF render.
  var pfs = useState(null);              var pendingFinalInvoice = pfs[0]; var setPendingFinalInvoice = pfs[1];
  var fss = useState(false);             var finalSaving = fss[0];     var setFinalSaving = fss[1];

  var canManage = isOwner || hasAdminAccess(userEmail, ctx.firmSettings);

  var ds = useState(null);    var detailId = ds[0];     var setDetailId = ds[1];
  var sf = useState('all');   var statusFilter = sf[0]; var setStatusFilter = sf[1];

  // ── Final Invoices section state (2026-06-07 redesign) ────────────
  // Unified project filter (replaces the prior dual oipProjFilter /
  // projFilter setup that filtered Open separately from Drafts/Sent).
  // Large brand-style project buttons above the sub-tab nav drive this.
  var fpf = useState('all');
  var finProjFilter = fpf[0]; var setFinProjFilter = fpf[1];
  // Search across item / vendor / PO / FIN ref for Drafts / Ready / Sent
  // tabs (Open has its own item-search affordance once UX work lands).
  var fss2 = useState('');
  var finSearch = fss2[0]; var setFinSearch = fss2[1];
  // Sort dropdown — Date desc default. Project sort dropped because the
  // project buttons replace that lens.
  var fsb = useState('date_desc');
  var finSortBy = fsb[0]; var setFinSortBy = fsb[1];

  // Deposit-invoices visibility (2026-06-06 UX patch). DEPs are
  // bookkeeping artifacts on their way out under the billing redesign
  // (Phase 3 retires per-item DEPs entirely). Until then they pile up
  // in the All Invoices view and bury the Final Invoices section, which
  // is where actual action lives. Default-off toggle hides them; when
  // on, a binary Unpaid/Paid filter scopes the list. Both states persist
  // in localStorage so the bookkeeper's preference sticks across sessions.
  var sd = useState(function() {
    try { return localStorage.getItem('cs.invoicesTab.showDeposits') === '1'; }
    catch (_e) { return false; }
  });
  var showDeposits = sd[0]; var setShowDepositsState = sd[1];
  function setShowDeposits(next) {
    setShowDepositsState(next);
    try { localStorage.setItem('cs.invoicesTab.showDeposits', next ? '1' : '0'); }
    catch (_e) {}
  }
  var df = useState(function() {
    try { return localStorage.getItem('cs.invoicesTab.depositFilter') || 'unpaid'; }
    catch (_e) { return 'unpaid'; }
  });
  var depositFilter = df[0]; var setDepositFilterState = df[1];
  function setDepositFilter(next) {
    setDepositFilterState(next);
    try { localStorage.setItem('cs.invoicesTab.depositFilter', next); }
    catch (_e) {}
  }

  // Honor detail jumps from other tabs — opens the invoice detail modal.
  useDetailJump('invoices', function(id) { setDetailId(id); });
  var pj = useState('all');   var projFilter = pj[0];   var setProjFilter = pj[1];

  // Project list derived from invoices
  var availableProjects = useMemo(function() {
    var s = new Set();
    invoices.forEach(function(inv) {
      var proj = inv.project || inv.project_label || '';
      if (proj) s.add(proj);
    });
    return Array.from(s).sort();
  }, [invoices]);

  // Section-specific derived lists for the unified Invoices view.
  // Deposit section: deposits only, status + project filters applied.
  // Sent-final section: finals with status='sent', project filter applied
  //   (status is implicit — "sent" IS the section). Status filter is NOT
  //   re-applied here so chip selections never mask the sent list.
  // Project-filtered DEPs before the Unpaid/Paid split. Split below so
  // the button labels can carry live counts and the rendered list reads
  // from the same source. The legacy `statusFilter` chip path is dead
  // here (Deposit section uses the binary Unpaid/Paid filter only since
  // 2026-06-06) but the state lives on for the other invoice sections
  // that haven't migrated to this pattern.
  var depositsForProject = useMemo(function() {
    return invoices
      .filter(function(inv) { return inv.invoiceType === 'deposit'; })
      .filter(function(inv) {
        if (projFilter === 'all') return true;
        var proj = inv.project || inv.project_label || '';
        return proj === projFilter;
      })
      .slice()
      .sort(function(a, b) {
        return (b.createdAt || b.created_at || '').localeCompare(a.createdAt || a.created_at || '');
      });
  }, [invoices, projFilter]);

  // Unpaid = anything not fully paid (draft / sent / partial / unpaid).
  // Paid = status === 'paid'. Mapper-normalized lowercase per Fix D
  // Phase 1.3 (2026-05-31) — no case-insensitive check needed.
  var unpaidDeposits = useMemo(function() {
    return depositsForProject.filter(function(inv) { return inv.status !== 'paid'; });
  }, [depositsForProject]);
  var paidDeposits = useMemo(function() {
    return depositsForProject.filter(function(inv) { return inv.status === 'paid'; });
  }, [depositsForProject]);
  var depositInvoices = depositFilter === 'paid' ? paidDeposits : unpaidDeposits;

  // Status-bucket filters for the FIN sub-tabs. All share the unified
  // finProjFilter + finSearch + finSortBy state defined above so the
  // project buttons / search / sort act consistently across Draft /
  // Ready / Sent. Open uses its own picker (items in flight, not
  // invoices) but inherits finProjFilter via the OrdersInProcess prop.
  function _finMatchesFilters(inv) {
    var type = inv.invoiceType || 'final'; // legacy rows default to final
    if (type !== 'final') return false;
    if (finProjFilter !== 'all') {
      var proj = inv.project || inv.project_label || '';
      if (proj !== finProjFilter) return false;
    }
    var q = (finSearch || '').trim().toLowerCase();
    if (q) {
      // 2026-08-26 (user-reported "search isn't working well"): the
      // haystack now also covers PO refs, PO vendor names, and the
      // underlying items' names + SKUs — derived from inv.itemIds via the
      // same helpers the table columns use, so anything visible in a row
      // is findable by the search box. Line-item fields stay as the
      // fallback for legacy rows with no itemIds.
      var poSum = finPoSummary(inv, allItems, purchaseOrders);
      var invItems = (inv.itemIds || inv.item_ids || []).map(function(iid) {
        return allItems.find(function(it) { return it.id === iid; });
      }).filter(Boolean);
      var haystack = [
        inv.invoiceRef || inv.invoice_ref || '',
        inv.clientName || inv.client_name || '',
        inv.project || inv.project_label || '',
        poSum.poRefs.join(' '),
        poSum.vendorNames.join(' '),
        invItems.map(function(it) {
          return [it.item, it.productName, it.itemNumber, it.vendor].filter(Boolean).join(' ');
        }).join(' '),
        (inv.lineItems || inv.line_items || [])
          .map(function(li) { return (li && [li.item, li.productName, li.description].filter(Boolean).join(' ')) || ''; }).join(' '),
      ].join(' ').toLowerCase();
      if (haystack.indexOf(q) < 0) return false;
    }
    return true;
  }
  function _finSort(a, b) {
    switch (finSortBy) {
      case 'date_asc':    return (a.createdAt || a.created_at || '').localeCompare(b.createdAt || b.created_at || '');
      case 'amount_desc': return (parseFloat(b.amountDue || b.amount_due) || 0) - (parseFloat(a.amountDue || a.amount_due) || 0);
      case 'amount_asc':  return (parseFloat(a.amountDue || a.amount_due) || 0) - (parseFloat(b.amountDue || b.amount_due) || 0);
      case 'vendor':      return ((a.clientName || a.client_name || '').localeCompare(b.clientName || b.client_name || ''));
      case 'date_desc':
      default:            return (b.createdAt || b.created_at || '').localeCompare(a.createdAt || a.created_at || '');
    }
  }

  var sentFinalInvoices = useMemo(function() {
    return invoices
      .filter(function(inv) {
        if (inv.status !== 'sent' && inv.status !== 'partial' && inv.status !== 'paid') return false;
        return _finMatchesFilters(inv);
      })
      .slice()
      .sort(_finSort);
  }, [invoices, finProjFilter, finSearch, finSortBy, allItems, purchaseOrders]);

  // Draft finals — committed via Save & Commit (finalizeInvoiceAtomic),
  // not yet finalized for delivery.
  var draftFinalInvoices = useMemo(function() {
    return invoices
      .filter(function(inv) {
        if (inv.status !== 'draft') return false;
        return _finMatchesFilters(inv);
      })
      .slice()
      .sort(_finSort);
  }, [invoices, finProjFilter, finSearch, finSortBy, allItems, purchaseOrders]);

  // Ready finals — finalized via Mark Ready, awaiting bundling into
  // the next billing summary. The BillingSummary Mark-Sent cascade
  // flips them to 'sent' on summary delivery (Commit 1 of the FIN
  // restructure work, 2026-06-26).
  var readyFinalInvoices = useMemo(function() {
    return invoices
      .filter(function(inv) {
        if (inv.status !== 'ready') return false;
        return _finMatchesFilters(inv);
      })
      .slice()
      .sort(_finSort);
  }, [invoices, finProjFilter, finSearch, finSortBy, allItems, purchaseOrders]);

  // ── Actions ──
  function markSent(inv) {
    saveInvoice(Object.assign({}, inv, { status: 'sent', sentAt: new Date().toISOString() }));
  }
  // Mark Ready (2026-06-07 lifecycle scaffold). Finalizes a draft FIN
  // for inclusion in the next billing summary without sending to the
  // client directly. The summary's Mark Sent cascade flips
  // ready/draft FINs to status='sent' + populates sent_at; that's the
  // canonical delivery event. Direct Mark Sent on a FIN remains
  // available for off-cycle delivery.
  function markReady(inv) {
    saveInvoice(Object.assign({}, inv, { status: 'ready' }));
  }
  // Cascade item statuses when an invoice transitions to paid.
  // - Deposit paid: items at Client Approved advance to Deposit Paid, AND
  //   assembly peers cascade. Peers always move together at deposit time
  //   so a late-added Workroom doesn't get stuck at Client Approved while
  //   its Frame/Fabric/Trim siblings reach Deposit Paid (which would block
  //   the Workroom from PO eligibility).
  // - Final paid: items at Order Confirmed/Received/Installed advance to
  //   Invoiced. NO peer cascade -- final invoices are batched by delivery,
  //   so a peer not on this invoice hasn't shipped yet. Cascading peers
  //   would silently mark un-shipped items as Invoiced.
  function cascadePaidItems(inv, allocsOverride) {
    if (!updateItem) return;
    // Cascade rules in computeCascadeTargets (utils.js) — pure, tested in
    // cascadePaidItems.test.js. This function is just the I/O wrapper.
    var targets = computeCascadeTargets(inv, allItems);
    // Phase fix 2026-06-27 — partial-deposit cascade. For deposit
    // invoices, also advance items whose per-item paid share covers
    // their expected deposit even if the invoice itself isn't fully
    // paid yet. Closes the Kumiko gap (paid 96% → items stuck at
    // Client Approved). computePartialCascadeTargets is a no-op for
    // final invoices + for items already past Client Approved.
    var allocsForCheck = allocsOverride || paymentAllocations;
    var partialTargets = computePartialCascadeTargets(inv, allItems, invoices, allocsForCheck);
    // Merge + dedupe (an item shouldn't appear in both sets, but be
    // defensive against future cascade-rule overlaps).
    var seen = new Set(targets.map(function(t) { return t.itemId; }));
    partialTargets.forEach(function(t) {
      if (!seen.has(t.itemId)) { targets.push(t); seen.add(t.itemId); }
    });
    if (targets.length === 0) return;
    var promises = targets.map(function(t) {
      return updateItem(t.itemId, { status: t.newStatus });
    });
    Promise.all(promises).then(function(results) {
      var failed = results.filter(function(r) { return r === false; }).length;
      if (failed > 0) console.error('cascadePaidItems: ' + failed + ' of ' + results.length + ' updates failed');
    });
  }

  // Persist invoice status off the relational allocation set, and run the
  // item cascade if the invoice just crossed fully-paid.
  //
  // Phase fix 2026-06-27 — partial-payment cascade. Pre-fix the cascade
  // only fired on the full-paid transition (`isFullyPaid && !wasFullyPaid`).
  // The Kumiko incident (paid 96% of a deposit, items stuck at Client
  // Approved) motivated extending the cascade to fire whenever the
  // ALLOCATION SET CHANGES on a deposit invoice. The cascade's internal
  // partial-target logic (computePartialCascadeTargets) only advances
  // items whose per-item paid share covers their expected deposit
  // within tolerance — items partially paid stay at Client Approved.
  // For final invoices the cascade is unchanged (still fires only on
  // full-paid via the existing computeCascadeTargets path).
  function syncInvoiceStatusFromAllocations(inv, allocsForCascadeCheck) {
    var amt = parseFloat(inv.amountDue || inv.amount) || 0;
    var nextStatus = derivePaidStatus(allocsForCascadeCheck, 'invoice', inv.id, amt, inv.status);
    var prevPaid = calcPaidForDocument(paymentAllocations, 'invoice', inv.id);
    var nextPaid = calcPaidForDocument(allocsForCascadeCheck, 'invoice', inv.id);
    var wasFullyPaid = prevPaid >= amt - 0.005 && amt > 0;
    var isFullyPaid  = nextStatus === 'paid';
    if (nextStatus !== inv.status) {
      saveInvoice(Object.assign({}, inv, { status: nextStatus }));
    }
    // Cascade fires on full-paid transition (legacy behavior, unchanged)
    // OR whenever the allocation set changed on a DEPOSIT invoice (new
    // partial-cascade path). The inner cascadePaidItems handles both —
    // it's a no-op when no targets need advancing.
    var allocsChanged = Math.abs(nextPaid - prevPaid) > 0.005;
    if ((isFullyPaid && !wasFullyPaid)
        || (inv.invoiceType === 'deposit' && allocsChanged)) {
      cascadePaidItems(inv, allocsForCascadeCheck);
    }
  }

  // Close a $0 FIN. Used when a 100%-upfront proposal's DEP collected
  // the full client bill, so the generated FIN sits at amount_due=$0.
  // derivePaidStatus refuses to auto-flip such FINs to 'paid' (the
  // `amt > 0` guard at utils.js:7317 is deliberate — it prevents draft
  // $0 rows from being treated as settled). This is the explicit-close
  // affordance: bookkeeper deliberately closes the FIN once they have
  // verified all vendor receipts are in and no further adjustments will
  // queue. Future vendor-fee adjustments on the underlying POs would
  // need a supplemental FIN. 2026-06-13.
  async function closeFinAtZero(inv) {
    if (!saveInvoice) return;
    var amt = parseFloat(inv.amountDue || inv.amount_due || inv.amount) || 0;
    if (amt > 0.005) return; // not a $0 row — refuse
    if (inv.status === 'paid') return; // already closed
    saveInvoice(Object.assign({}, inv, { status: 'paid' }));
  }

  // Mark Paid: build a single-allocation client_payment for the remaining
  // balance, persist it, then sync status + cascade items.
  async function markPaid(inv) {
    if (!recordClientPayment) return;
    var amt = parseFloat(inv.amountDue || inv.amount) || 0;
    if (amt <= 0) return;
    var alreadyPaid = calcPaidForDocument(paymentAllocations, 'invoice', inv.id);
    var remaining = Math.max(0, amt - alreadyPaid);
    if (remaining <= 0.005) return; // already fully paid

    var payment = {
      clientId:      inv.clientId || inv.client_id || '',
      projectLabel:  inv.project  || inv.project_label || '',
      paymentDate:   new Date().toISOString().slice(0, 10),
      amountTotal:   remaining,
      method:        '',
      bankReference: '',
      reference:     'Marked paid',
      notes:         '',
      recordedBy:    userEmail || '',
    };
    var allocations = [{ documentType: 'invoice', documentId: inv.id, amount: remaining }];
    var res = await recordClientPayment(payment, allocations);
    if (res && res.ok) {
      var nextAllocs = paymentAllocations.concat(res.allocations || []);
      syncInvoiceStatusFromAllocations(inv, nextAllocs);
    }
  }

  // Unmark paid: remove every allocation pointing at this invoice. The
  // status walks back to 'sent' (or 'draft' if it was draft to begin with).
  async function unpay(inv) {
    if (!removePaymentAllocation) return;
    var allocs = paymentAllocations.filter(function(a) {
      return a.documentType === 'invoice' && a.documentId === String(inv.id);
    });
    for (var i = 0; i < allocs.length; i++) {
      // sequential to keep optimistic state consistent
      // eslint-disable-next-line no-await-in-loop
      await removePaymentAllocation(allocs[i].id);
    }
    var nextStatus = inv.status === 'draft' ? 'draft' : 'sent';
    if (nextStatus !== inv.status) {
      saveInvoice(Object.assign({}, inv, { status: nextStatus }));
    }
  }

  // PaymentsPanel onRecordPayment(payment, allocations) bridge. Persists the
  // pair via context, then syncs invoice status + item cascade.
  async function onPanelRecordPayment(inv, payment, allocations) {
    if (!recordClientPayment) return;
    var res = await recordClientPayment(payment, allocations);
    if (res && res.ok) {
      var nextAllocs = paymentAllocations.concat(res.allocations || []);
      syncInvoiceStatusFromAllocations(inv, nextAllocs);
    }
  }

  // PaymentsPanel onRemoveAllocation(allocationId) bridge. After removal,
  // the invoice may drop below fully-paid → walk status back.
  async function onPanelRemoveAllocation(inv, allocationId) {
    if (!removePaymentAllocation) return;
    await removePaymentAllocation(allocationId);
    var nextAllocs = paymentAllocations.filter(function(a) { return a.id !== allocationId; });
    var amt = parseFloat(inv.amountDue || inv.amount) || 0;
    var nextStatus = derivePaidStatus(nextAllocs, 'invoice', inv.id, amt, inv.status);
    if (nextStatus !== inv.status) {
      saveInvoice(Object.assign({}, inv, { status: nextStatus }));
    }
    // NOTE: items are NOT walked back when a payment is removed — once an
    // item is at Deposit Paid / Invoiced it has downstream consequences
    // (PO issued, vendor activity) that don't unwind cleanly. The cascade
    // is intentionally one-way (forward on cross-up; no walk-back on
    // cross-down). If you need to undo a payment that has already triggered
    // a PO, do it manually via the item's status field.
  }

  // BankDepositModal onSaved bridge. Dispatches per allocation document
  // type so each touched document gets its proper side effects (status
  // flip + item cascade for invoices; status flip for hours invoices;
  // status flip + retainer auto-draw for billing summaries). Without
  // this, the modal would write the payment but every dependent system
  // would stay stale until the next page refresh.
  async function onBankDepositSaved(payment, savedAllocations) {
    var nextAllocs = paymentAllocations.concat(savedAllocations || []);

    for (var i = 0; i < (savedAllocations || []).length; i++) {
      var alloc = savedAllocations[i];
      if (alloc.documentType === 'invoice') {
        var inv = invoices.find(function(x) { return x.id === alloc.documentId; });
        if (inv) syncInvoiceStatusFromAllocations(inv, nextAllocs);
      } else if (alloc.documentType === 'hours_invoice') {
        // Hours invoice statuses: Draft / Final / Paid (capitalized).
        var hr = await supabase.from('hours_invoices')
          .select('total_amount, status')
          .eq('invoice_number', alloc.documentId).maybeSingle();
        if (hr.data) {
          var hrAmt = parseFloat(hr.data.total_amount) || 0;
          var hrPaid = nextAllocs
            .filter(function(a) { return a.documentType === 'hours_invoice' && a.documentId === alloc.documentId; })
            .reduce(function(s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
          var hrFullyPaid = hrPaid >= hrAmt - 0.005 && hrAmt > 0;
          var hrIsPartial = !hrFullyPaid && hrPaid > 0;
          var hrNextStatus = hr.data.status;
          if (hrFullyPaid) hrNextStatus = 'Paid';
          else if (hrIsPartial) hrNextStatus = 'Partial';
          else if (hr.data.status === 'Paid' || hr.data.status === 'Partial') hrNextStatus = 'Final';
          if (hrNextStatus !== hr.data.status) {
            // eslint-disable-next-line no-await-in-loop
            await supabase.from('hours_invoices')
              .update({ status: hrNextStatus, updated_at: new Date().toISOString() })
              .eq('invoice_number', alloc.documentId);
          }
        }
      } else if (alloc.documentType === 'billing_summary') {
        // eslint-disable-next-line no-await-in-loop
        var bs = await supabase.from('billing_summaries')
          .select('id, status, design_amount, additional_services, expenses, project_label')
          .eq('id', alloc.documentId).maybeSingle();
        if (bs.data) {
          // billing_summaries has no amount_due column — compute from the
          // canonical bsSubtotal formula (design + additional + expenses,
          // plus reimbursables tagged onto this summary for retainer
          // projects since 2026-05-11).
          var bsProj = allProjects.find(function(p) { return p.label === bs.data.project_label; });
          var bsProjHasRetainer = !!(bsProj && bsProj.hasRetainer);
          var bsAmt = calcBillingSummaryAmount(bs.data, invoices, bsProjHasRetainer);
          var bsNextStatus = derivePaidStatus(nextAllocs, 'billing_summary', bs.data.id, bsAmt, bs.data.status);
          if (bsNextStatus !== bs.data.status) {
            // eslint-disable-next-line no-await-in-loop
            await supabase.from('billing_summaries')
              .update({ status: bsNextStatus }).eq('id', bs.data.id);
          }
          // NO retainer draw here. The draw fires at summary SEND
          // (persistRecord's sent block in BillingSummarySection) — the
          // 2026-07-01 pivot moved it out of the payment handlers. A
          // legacy payment-time draw copy survived here until 2026-07-27;
          // it could re-draw for pre-pivot summaries whose send never
          // drew. Any such backfill is a deliberate manual entry on the
          // Retainers page, never an automatic side effect of recording
          // a bank deposit. Guard: retainerDrawOnSend.test.js.
        }
      }
    }
  }

  async function deleteOne(inv) {
    if (!window.confirm('Delete invoice ' + (inv.invoiceRef || inv.invoice_ref || inv.id) + '? This cannot be undone.')) return;
    var result = await deleteInvoice(inv.id);
    if (!result || !result.ok) {
      // Hard block from delete guard — show the actual reason (paid / has
      // allocations / sent in summary) instead of silently failing.
      window.alert('Cannot delete:\n\n' + (result && result.error || 'Unknown error'));
      return;
    }
    if (detailId === inv.id) setDetailId(null);
  }

  // ── Detail modal ──
  if (detailId) {
    var open = invoices.find(function(x) { return x.id === detailId; });
    if (!open) { setDetailId(null); return null; }
    var entriesForOpen = paymentEntriesForDocument(paymentAllocations, clientPayments, 'invoice', open.id);
    return (
      <InvoiceDetail
        invoice={open}
        clientPayments={clientPayments}
        clients={allClients}
        projects={allProjects}
        proposals={allProposals}
        items={allItems}
        purchaseOrders={purchaseOrders}
        invoices={invoices}
        billingSummaries={billingSummaries}
        canManage={canManage}
        userEmail={userEmail}
        entries={entriesForOpen}
        paymentAllocations={paymentAllocations}
        onClose={function() { setDetailId(null); }}
        onMarkSent={function() { markSent(open); }}
        onMarkReady={function() { markReady(open); }}
        onMarkPaid={function() { markPaid(open); }}
        onUnpay={function() { unpay(open); }}
        // onRecordPayment deliberately NOT passed since 2026-07-04 —
        // recording moved to Billing History's package wizard; the
        // detail's PaymentsPanel is history + corrections only, and it
        // hides its "+ Record Payment" button when the prop is absent.
        onUpdatePayment={async function(paymentId, allocationId, patch) {
          if (!updateClientPayment) return;
          var res = await updateClientPayment(paymentId, allocationId, patch);
          if (res && !res.ok) window.alert('Could not update payment: ' + (res.error && res.error.message || 'unknown'));
        }}
        onRemoveAllocation={function(allocId) { onPanelRemoveAllocation(open, allocId); }}
        onDelete={function() { deleteOne(open); }}
        // Snapshot spec refresh (2026-09-11). The child computes the diff,
        // confirms it and re-renders the archive; the row write lives here
        // next to every other invoice write.
        onSnapshotRefreshed={function(nextLines) {
          if (saveInvoice) saveInvoice(Object.assign({}, open, { lineItems: nextLines }));
        }}
      />
    );
  }

  // ── List view ──
  return (
    <div style={S.wrap}>

      {/* Sub-tab navigation. Two groups separated by a divider:
            Compose:    Billing Summary, Billing History
            Components: Invoices, Payments, Design Hours, Receipts, Change Orders, Retainers, Credits
          The former "Orders in Process" sub-tab is gone — its functionality
          lives inside Invoices → FINAL INVOICES → open. */}
      <div style={{ display: 'flex', alignItems: 'center', height: 38, padding: '0 14px', gap: 0, borderBottom: '0.5px solid #D8D7D2', background: '#FFFFFF', flexShrink: 0 }}>
        <SubTabBtn active={subTab === 'summary'}   onClick={function() { setSubTab('summary'); }}>Billing Summary</SubTabBtn>
        <SubTabBtn active={subTab === 'sentpackages'} onClick={function() { setSubTab('sentpackages'); }}>Billing History</SubTabBtn>
        <div style={{ width: 1, height: 18, background: '#D8D7D2', margin: '0 12px' }} aria-hidden="true" />
        <SubTabBtn active={subTab === 'all'}       onClick={function() { setSubTab('all'); }}>Invoices</SubTabBtn>
        <SubTabBtn active={subTab === 'payments'}  onClick={function() { setSubTab('payments'); }}>Payments</SubTabBtn>
        <SubTabBtn active={subTab === 'hours'}     onClick={function() { setSubTab('hours'); }}>Design Hours</SubTabBtn>
        <SubTabBtn active={subTab === 'receipts'}  onClick={function() { setSubTab('receipts'); }}>Receipts</SubTabBtn>
        <SubTabBtn active={subTab === 'changeorders'} onClick={function() { setSubTab('changeorders'); }}>Change Orders</SubTabBtn>
        <SubTabBtn active={subTab === 'retainers'} onClick={function() { setSubTab('retainers'); }}>Retainers</SubTabBtn>
        <SubTabBtn active={subTab === 'credits'}   onClick={function() { setSubTab('credits'); }}>Credits</SubTabBtn>
      </div>

      {subTab === 'hours'     && <DesignerHoursSection />}
      {subTab === 'summary'   && <BillingSummarySection />}
      {/* Billing History records payments now (2026-07-02) — it gets the
          same cascade dispatcher BillingSummarySection's wizard used, so
          deposit→item→PO cascades keep firing on save. */}
      {subTab === 'sentpackages' && <BillingHistorySection onClientPaymentRecorded={onBankDepositSaved} />}
      {subTab === 'receipts'  && <ReceiptsTab />}
      {subTab === 'retainers' && <RetainersSection />}
      {subTab === 'credits'   && <ClientCreditsSection />}
      {subTab === 'changeorders' && <ChangeOrdersTab />}
      {subTab === 'payments'  && <PaymentsLedgerSection onSwitchSubTab={setSubTab} />}

      {subTab === 'all' && (
        <>

      {/* Toolbar — applies to both Deposit + Final sections */}
      <div style={S.toolbar}>
        <span style={S.pageTitle}>Invoices</span>
        <span style={{ fontSize: 11, color: '#888880' }}>· {depositInvoices.length} deposit · {sentFinalInvoices.length} final sent</span>
        <div style={S.vsep} />

        {/* Project filter — applies to BOTH sections */}
        {availableProjects.length > 0 && (
          <select
            value={projFilter}
            onChange={function(e) { setProjFilter(e.target.value); }}
            style={Object.assign({}, S.tbtnGhost, { paddingRight: 24, height: 28 })}>
            <option value="all">All projects</option>
            {availableProjects.map(function(p) { return <option key={p} value={p}>{p}</option>; })}
          </select>
        )}

        <div style={{ flex: 1 }} />

        {canManagePayments && (
          <button
            onClick={function() { setDepositModalOpen(true); }}
            style={Object.assign({}, S.tbtnPrimary, { background: '#1A5C38', border: '0.5px solid #1A5C38' })}>
            + Record Bank Deposit
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* ── DEPOSIT INVOICES ─────────────────────────────────────── */}
        {/* Hidden by default (2026-06-06 UX patch). DEPs are bookkeeping
            artifacts on their way out under the billing redesign; surfacing
            them by default buries the Final Invoices section. Collapsed
            header always renders so the bookkeeper knows DEPs exist and
            can expand on demand. Preference persists in localStorage. */}
        <div style={{ padding: '14px 18px 0 18px' }}>
          {!showDeposits ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>
                Deposit Invoices · {depositsForProject.length} hidden
              </div>
              <div style={{ flex: 1 }} />
              <button onClick={function() { setShowDeposits(true); }}
                style={{
                  height: 24, padding: '0 12px', borderRadius: 12,
                  border: '0.5px solid #D8D7D2', background: '#FFFFFF',
                  color: '#111111', fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                Show
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>
                  Deposit Invoices
                </div>
                <div style={{ flex: 1 }} />
                {/* Binary Unpaid / Paid filter (replaces the legacy 5-pill
                    All/Draft/Sent/Partial/Paid bar). Mutually exclusive — DEPs
                    are either fully paid or not, and the action set is the
                    unpaid bucket. */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {[
                    { value: 'unpaid', label: 'Unpaid · ' + unpaidDeposits.length },
                    { value: 'paid',   label: 'Paid · '   + paidDeposits.length },
                  ].map(function(f) {
                    var active = depositFilter === f.value;
                    return (
                      <button key={f.value}
                        onClick={function() { setDepositFilter(f.value); }}
                        style={{
                          height: 24, padding: '0 12px', borderRadius: 12,
                          border: active ? '1.5px solid #111111' : '0.5px solid #D8D7D2',
                          background: '#FFFFFF',
                          color: active ? '#111111' : '#888880',
                          fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                <button onClick={function() { setShowDeposits(false); }}
                  style={{
                    height: 24, padding: '0 12px', borderRadius: 12,
                    border: '0.5px solid #D8D7D2', background: '#FFFFFF',
                    color: '#888880', fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  Hide
                </button>
              </div>
              {depositInvoices.length === 0 ? (
                <div style={{ padding: '24px 0 32px', color: '#BBBBBB', fontSize: 12, fontStyle: 'italic', textAlign: 'center', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
                  {depositsForProject.length === 0
                    ? 'No deposit invoices yet. They auto-create when a proposal is approved.'
                    : 'No ' + depositFilter + ' deposit invoices in this scope.'}
                </div>
              ) : (
                <InvoiceTable rows={depositInvoices} onRowClick={function(inv) { setDetailId(inv.id); }} items={allItems} purchaseOrders={purchaseOrders} proposals={allProposals} invoices={invoices} billingSummaries={billingSummaries} />
              )}
            </>
          )}
        </div>

        {/* ── FINAL INVOICES ───────────────────────────────────────── */}
        <div style={{ padding: '28px 18px 18px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 12 }}>
            Final Invoices
          </div>

          {/* Project filter buttons — large, left-aligned, prominent.
              2026-06-07 redesign per bookkeeper request: project lens
              is the primary scope control; sub-tabs (Open/Draft/Ready/
              Sent) sit under it. Brand button geometry (32px height,
              16px radius). Active: 1.5px black border + black text on
              white; inactive: 0.5px hairline + muted text. No fills. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { value: 'all',         label: 'All Projects' },
              { value: '26-Hoback',   label: '26-Hoback' },
              { value: '26-Ptarmigan', label: '26-Ptarmigan' },
              { value: '26-Moskito',  label: '26-Moskito' },
            ].map(function(pb) {
              var active = finProjFilter === pb.value;
              return (
                <button key={pb.value}
                  onClick={function() { setFinProjFilter(pb.value); }}
                  style={{
                    height: 32, padding: '0 14px', borderRadius: 16,
                    border: active ? '1.5px solid #111111' : '0.5px solid #D8D7D2',
                    background: '#FFFFFF',
                    color: active ? '#111111' : '#888880',
                    fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  {pb.label}
                </button>
              );
            })}
          </div>

          {/* Search + sort row — secondary controls below project
              buttons. Search is fuzzy across invoice ref, client name,
              project, and line-item names. Sort dropdown drives all
              three FIN-row sub-tabs (Draft / Ready / Sent) consistently.
              2026-06-26: hidden when the Open sub-tab is active — that
              view (<OrdersInProcess/>) groups items by PO and doesn't
              consume finSearch / finSortBy, so the controls were inert
              and confusing (user-reported: "dropdown selection has no
              effect"). Project filter buttons above STILL apply across
              all four sub-tabs and stay visible. */}
          {finalSubView !== 'open' && (
            <div data-testid="fin-search-sort-row"
                 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                type="text"
                value={finSearch}
                onChange={function(e) { setFinSearch(e.target.value); }}
                placeholder="Search ref, vendor, item name, SKU, PO, client, project…" autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
                style={{
                  flex: 1, minWidth: 280, maxWidth: 480, height: 28,
                  padding: '0 10px',
                  border: '0.5px solid #D8D7D2', borderRadius: 4,
                  background: '#FFFFFF', color: '#111111',
                  fontSize: 11, fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
              <label style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Sort</label>
              <select
                value={finSortBy}
                onChange={function(e) { setFinSortBy(e.target.value); }}
                style={{
                  height: 28, padding: '0 8px',
                  border: '0.5px solid #D8D7D2', borderRadius: 4,
                  background: '#FFFFFF', color: '#111111',
                  fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
                }}>
                <option value="date_desc">Date · newest first</option>
                <option value="date_asc">Date · oldest first</option>
                <option value="amount_desc">Amount · high to low</option>
                <option value="amount_asc">Amount · low to high</option>
                <option value="vendor">Client (A→Z)</option>
              </select>
            </div>
          )}

          {/* Sub-tabs: Open / Drafts / Ready / Sent. Ready holds
              Mark-Ready'd FINs awaiting bundling into the next
              billing summary; the summary's Mark Sent cascade flips
              them to Sent on summary delivery (Commit 1 of the FIN
              restructure work, 2026-06-26). */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[
              { value: 'open',   label: 'Open' },
              { value: 'drafts', label: 'Drafts · ' + draftFinalInvoices.length },
              { value: 'ready',  label: 'Ready · '  + readyFinalInvoices.length },
              { value: 'sent',   label: 'Sent · '   + sentFinalInvoices.length },
            ].map(function(pn) {
              var active = finalSubView === pn.value;
              return (
                <button key={pn.value}
                  onClick={function() { setFinalSubView(pn.value); }}
                  style={{
                    height: 24, padding: '0 12px', borderRadius: 12,
                    border: active ? '1.5px solid #111111' : '0.5px solid #D8D7D2',
                    background: active ? '#111111' : '#FFFFFF',
                    color: active ? '#FFFFFF' : '#555552',
                    fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  {pn.label}
                </button>
              );
            })}
          </div>

          {finalSubView === 'open' && (
            <OrdersInProcess
              allItems={allItems}
              clientPayments={clientPayments}
              purchaseOrders={purchaseOrders}
              receipts={ctx.receipts || []}
              vendorPayments={ctx.vendorPayments || []}
              vendorPaymentItems={ctx.vendorPaymentItems || []}
              vendorCreditLedger={ctx.vendorCreditLedger || []}
              vendors={ctx.vendors || []}
              projects={allProjects}
              clients={allClients}
              proposals={allProposals}
              firmSettings={firmSettings}
              invoices={invoices}
              paymentAllocations={paymentAllocations}
              changeOrders={changeOrders}
              finalizeInvoiceAtomic={finalizeInvoiceAtomic}
              savePOToDb={ctx.savePOToDb}
              setPurchaseOrders={ctx.setPurchaseOrders}
              setInvoices={ctx.setInvoices}
              userEmail={userEmail}
              projFilter={finProjFilter === 'all' ? '' : finProjFilter}
              setProjFilter={function(v) { setFinProjFilter(v || 'all'); }}
              hideProjectSelect={true}
              clientPayments={clientPayments}
              selectedIds={selectedFinalIds}
              setSelectedIds={setSelectedFinalIds}
              pdfUrl={finalPdfUrl}
              setPdfUrl={setFinalPdfUrl}
              pdfFilename={finalPdfFilename}
              setPdfFilename={setFinalPdfFilename}
              loading={finalLoading}
              setLoading={setFinalLoading}
              err={finalErr}
              setErr={setFinalErr}
              pendingInvoice={pendingFinalInvoice}
              setPendingInvoice={setPendingFinalInvoice}
              saving={finalSaving}
              setSaving={setFinalSaving}
            />
          )}

          {finalSubView === 'drafts' && (
            draftFinalInvoices.length === 0 ? (
              <div style={{ padding: '24px 0', color: '#BBBBBB', fontSize: 12, fontStyle: 'italic', textAlign: 'center', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
                No draft final invoices match the current filters. Drafts land here when generated via Save &amp; Commit in the Open view.
              </div>
            ) : (
              <InvoiceTable rows={draftFinalInvoices} onRowClick={function(inv) { setDetailId(inv.id); }} items={allItems} purchaseOrders={purchaseOrders} proposals={allProposals} invoices={invoices} billingSummaries={billingSummaries} hiddenCols={['summary']} />
            )
          )}

          {finalSubView === 'ready' && (
            readyFinalInvoices.length === 0 ? (
              <div style={{ padding: '24px 0', color: '#BBBBBB', fontSize: 12, fontStyle: 'italic', textAlign: 'center', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
                No final invoices ready for billing. Ready FINs land here after Mark Ready on a Draft; they auto-bundle into the next billing summary and flip to Sent when the summary is delivered.
              </div>
            ) : (
              <InvoiceTable rows={readyFinalInvoices} onRowClick={function(inv) { setDetailId(inv.id); }} items={allItems} purchaseOrders={purchaseOrders} proposals={allProposals} invoices={invoices} billingSummaries={billingSummaries} hiddenCols={['summary']} />
            )
          )}

          {finalSubView === 'sent' && (
            sentFinalInvoices.length === 0 ? (
              <div style={{ padding: '24px 0', color: '#BBBBBB', fontSize: 12, fontStyle: 'italic', textAlign: 'center', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
                No final invoices match the current filters. Sent FINs include both directly-sent (legacy model) and billing-summary-delivered (new model).
              </div>
            ) : (
              <SentFinalInvoicesList
                rows={sentFinalInvoices}
                onRowClick={function(inv) { setDetailId(inv.id); }}
                paymentAllocations={paymentAllocations}
                clientPayments={clientPayments}
                canManagePayments={canManagePayments}
                closeFinAtZero={closeFinAtZero}
                items={allItems}
                purchaseOrders={purchaseOrders}
                proposals={allProposals}
                invoices={invoices}
                billingSummaries={billingSummaries}
              />
            )
          )}
        </div>

      </div>

        </>
      )}

      <BankDepositModal
        open={depositModalOpen}
        presetProjectLabel={projFilter !== 'all' ? projFilter : ''}
        onClose={function() { setDepositModalOpen(false); }}
        onSaved={onBankDepositSaved} />
    </div>
  );
}

// ── SUB-TAB BUTTON ───────────────────────────────────────────────────────────

function SubTabBtn(p) {
  return (
    <button
      onClick={p.onClick}
      style={{
        height: 38, padding: '0 16px', border: 'none', background: 'transparent',
        borderBottom: p.active ? '2px solid #111111' : '2px solid transparent',
        color: p.active ? '#111111' : '#888880',
        fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
        cursor: 'pointer', whiteSpace: 'nowrap', marginBottom: -0.5,
      }}>
      {p.children}
    </button>
  );
}

// ── ORDERS IN PROCESS SUB-TAB ────────────────────────────────────────────────
// Items past Client Approved that haven't been final-invoiced yet, grouped by
// PO. Bookkeeper picks the items that shipped together and fires a final
// invoice for the batch via /api/generate-deposit-invoice (invoiceType=final).

// ── SENT FINAL INVOICES LIST (2026-06-07) ──────────────────────────────────
// Replaces the generic InvoiceTable for the Sent sub-tab. Surfaces the
// per-invoice payment state (Paid / Balance columns derived live from
// payment_allocations per Rule 10). Read-only on payments since
// 2026-07-04: recording moved to Billing History's package preview
// (wizard-guided, allocation-aware) — this list keeps only the
// "Close at $0" affordance for 100%-upfront FINs. The Reconciliation
// view's "Client Paid" column reads MAX(payment_allocations.created_at)
// for the FIN, so wizard-recorded payments surface there immediately.
//
// Includes FINs sent both directly (legacy model) and via billing
// summary delivery (new model — once the summary-send cascade flips
// bundled ready/draft FINs to status='sent', they appear here too).

function SentFinalInvoicesList(p) {
  // For column (2026-08-26, Cimon: "I need to be able to identify what the
  // items are") — same desc + SKU-subline cell as the Ready/Drafts
  // InvoiceTable, derived by the same helpers so the two lists can't drift.
  var refsBag = {
    items: p.items || [], proposals: p.proposals || [],
    purchaseOrders: p.purchaseOrders || [], invoices: p.invoices || [],
    billingSummaries: p.billingSummaries || [],
  };
  // tableLayout:'fixed' + explicit col widths: the compact columns hold
  // their size and ALL leftover width goes to For — auto layout was
  // spreading the slack as dead space between PO # / PO Vendor / Client.
  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 132 }} />{/* Ref */}
          <col style={{ width: 88 }} />{/* Project */}
          <col />{/* For — takes the remainder */}
          <col style={{ width: 118 }} />{/* PO # */}
          <col style={{ width: 148 }} />{/* PO Vendor */}
          <col style={{ width: 110 }} />{/* Client */}
          <col style={{ width: 92 }} />{/* Amount */}
          <col style={{ width: 92 }} />{/* Paid */}
          <col style={{ width: 92 }} />{/* Balance */}
          <col style={{ width: 70 }} />{/* Status */}
          <col style={{ width: 94 }} />{/* Date */}
          <col style={{ width: 92 }} />{/* action */}
        </colgroup>
        <thead>
          <tr>
            <th style={S.th}>Ref</th>
            <th style={S.th}>Project</th>
            <th style={S.th}>For</th>
            <th style={S.th}>PO #</th>
            <th style={S.th}>PO Vendor</th>
            <th style={S.th}>Client</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Amount</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Paid</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Balance</th>
            <th style={S.th}>Status</th>
            <th style={S.th}>Date</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>{/* action */}</th>
          </tr>
        </thead>
        <tbody>
          {p.rows.map(function(inv) {
            var ref      = inv.invoiceRef    || inv.invoice_ref    || '\u2014';
            var project  = inv.project       || inv.project_label  || '\u2014';
            var client   = inv.clientName    || inv.client_name    || '';
            // Class C invoices (Design Manager imports) have amount_due=0
            // and the original total on the legacy `amount` column —
            // mapper surfaces both, fallback chain renders correctly.
            var amount   = parseFloat(inv.amountDue || inv.amount_due || inv.amount) || 0;
            var paid     = calcPaidForDocument(p.paymentAllocations || [], 'invoice', inv.id);
            var balance  = Math.max(0, amount - paid);
            var status   = inv.status || 'sent';
            var date     = (inv.sentAt || inv.sent_at || inv.createdAt || inv.created_at || '').slice(0, 10);
            var statusColor = status === 'paid' ? '#1A5C38'
                            : status === 'partial' ? '#D4007A'
                            : status === 'sent' ? '#111111' : '#888880';
            // PO derivation — walks FIN.itemIds → items → poNumber → POs.
            // 1 FIN = 1 vendor = 1 PO going forward; legacy multi-PO FINs
            // render as comma-joined.
            var poSum = finPoSummary(inv, p.items || [], p.purchaseOrders || []);
            var poRefDisplay    = poSum.poRefs.length    ? poSum.poRefs.join(', ')    : '\u2014';
            var poVendorDisplay = poSum.vendorNames.length ? poSum.vendorNames.join(', ') : '\u2014';
            var desc = invoiceDescription(inv);
            var xr   = invoiceCrossRefs(inv, refsBag);
            return (
              <React.Fragment key={inv.id}>
                <tr onClick={function() { p.onRowClick(inv); }}
                  style={{ cursor: 'pointer', borderBottom: '0.5px solid #f0f0f0' }}>
                  <td style={Object.assign({}, S.td, { fontFamily: 'monospace' })}>{ref}</td>
                  <td style={S.td}>{project}</td>
                  <td style={S.td} title={desc}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desc}</div>
                    {xr.skus.length > 0 && (
                      <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#888880', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                           title={xr.skus.join(', ')}>
                        {xr.skus.join(' \u00b7 ')}
                      </div>
                    )}
                  </td>
                  <td style={Object.assign({}, S.td, { fontFamily: 'monospace', color: poSum.poRefs.length ? '#111111' : '#888880' })} title={poRefDisplay}>{poRefDisplay}</td>
                  <td style={Object.assign({}, S.td, { color: poSum.vendorNames.length ? '#111111' : '#888880' })} title={poVendorDisplay}>{poVendorDisplay}</td>
                  <td style={S.td}>{client}</td>
                  <td style={Object.assign({}, S.td, { textAlign: 'right' })}>{fmtMoney(amount)}</td>
                  <td style={Object.assign({}, S.td, { textAlign: 'right', color: paid > 0 ? '#1A5C38' : '#888880' })}>{fmtMoney(paid)}</td>
                  <td style={Object.assign({}, S.td, { textAlign: 'right', color: balance > 0 ? '#D4007A' : '#888880' })}>{fmtMoney(balance)}</td>
                  <td style={Object.assign({}, S.td, { color: statusColor, textTransform: 'capitalize' })}>{status}</td>
                  <td style={S.td}>{date}</td>
                  <td style={Object.assign({}, S.td, { textAlign: 'right' })} onClick={function(e) { e.stopPropagation(); }}>
                    {/* Payment recording lives in Billing History's package
                        preview ("Record payment", upper right) — one
                        recording surface, wizard-guided. The old inline
                        per-FIN recorder here was removed 2026-07-04
                        (Cimon: "now we are only recording payment on
                        billing history"). Off-package payments still have
                        the Bank Deposit modal in the All Invoices toolbar. */}
                    {(p.canManagePayments && amount <= 0.005 && status !== 'paid' && p.closeFinAtZero ? (
                      <button onClick={function() {
                        var proceed = window.confirm(
                          'Close ' + ref + ' at $0?\n\n' +
                          'Use this when the deposit collected the full client bill (100%-upfront proposals) and no further vendor adjustments are expected on the underlying PO(s).\n\n' +
                          'After closing, any future vendor-fee adjustments queued on the source PO(s) won\u2019t flow into this FIN — they\u2019ll need a supplemental FIN.'
                        );
                        if (proceed) p.closeFinAtZero(inv);
                      }}
                        style={{
                          height: 24, padding: '0 10px', borderRadius: 4,
                          border: '0.5px solid #888880', background: '#FFFFFF',
                          color: '#555552',
                          fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>
                        Close at $0
                      </button>
                    ) : null)}
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── ARCHIVED FIN PDF UPLOAD (2026-06-07) ───────────────────────────────────
// Uploads the rendered FIN PDF blob to Supabase Storage at Save & Commit
// time and writes the storage path onto the invoices row. The stored PDF
// is the audit-grade "this is what we sent" record; regenerated PDFs
// can drift over time because some inputs (depositPaid via current
// allocations, etc.) are live-read. Bucket created in migration
// 20260607_create_invoices_storage_bucket.sql; admin-only writes via
// current_admin_emails() RLS gate.
//
// Path: invoices/{yyyy-mm}/{invoice_id}.pdf — mirrors the receipts
// bucket convention. Idempotent via upsert=true so re-saving an invoice
// (e.g., after an Adjustments edit, future work) replaces the stored
// PDF cleanly.
//
// Best-effort: upload failure does NOT block Save & Commit. The invoice
// row persists via finalizeInvoiceAtomic regardless; only the
// pdf_storage_path stays empty if the upload fails. Logged to console
// so the bookkeeper can investigate later.
// `existingPath` (2026-09-11): when an invoice already has an archive, the
// replacement must land on the SAME object, or a refresh in a later month
// would mint a second file and orphan the first.
async function uploadFinPdfArchive(invoiceId, blob, existingPath) {
  if (!invoiceId || !blob) return null;
  var month = new Date().toISOString().slice(0, 7);
  var path  = existingPath || (month + '/' + invoiceId + '.pdf');
  try {
    var up = await supabase.storage.from('invoices').upload(path, blob, {
      contentType: 'application/pdf',
      upsert: true,
    });
    if (up.error) {
      console.error('[fin-pdf-archive] storage upload failed:', up.error);
      return null;
    }
    var dbUpdate = await supabase.from('invoices')
      .update({ pdf_storage_path: path })
      .eq('id', invoiceId);
    if (dbUpdate.error) {
      console.error('[fin-pdf-archive] row update failed:', dbUpdate.error);
      return null;
    }
    return path;
  } catch (e) {
    console.error('[fin-pdf-archive] exception:', e);
    return null;
  }
}

function OrdersInProcess(p) {
  // Use the canonical fmtMoney from utils.js so the picker matches
  // every other invoice surface (always 2 decimals; "$0.00" not "$0").
  // The earlier local fmt used maximumFractionDigits: 0, which surfaced
  // "$1,014" instead of "$1,014.00" — inconsistent with the rest of
  // Client Invoices and the underlying invoice records.
  var fmt = fmtMoney;

  // ── Adjustments (2026-06-06 patch) ──────────────────────────────────
  // Pass-through deltas Cimon layers onto a FIN at generation time
  // (late tariff, freight overage, crating overage). Empty by default
  // — visible in the preview overlay as an editor pane; user edits and
  // clicks "Regenerate Preview" to rebuild the PDF with the new shape.
  // Cleared on Cancel and after a successful Save & Commit.
  //
  // Per-row shape matches BILLING_REDESIGN_SPEC.md §3:
  //   { description, amount, taxable, source_ref }
  // source_ref is null in this patch; Phase 4 FIN Builder populates from
  // receipt UUIDs.
  var _adjState = React.useState([]);
  var adjustments = _adjState[0];
  var setAdjustments = _adjState[1];

  function addAdjustment() {
    setAdjustments(adjustments.concat([{ description: '', amount: 0, taxable: true, source_ref: null }]));
  }
  function updateAdjustment(idx, patch) {
    setAdjustments(adjustments.map(function(a, i) { return i === idx ? Object.assign({}, a, patch) : a; }));
  }
  function removeAdjustment(idx) {
    setAdjustments(adjustments.filter(function(_, i) { return i !== idx; }));
  }

  var billableParents = (p.allItems || []).filter(isBillable);
  var inProcess = billableParents.filter(function(it) {
    if (!isItemInProcess(it, p.invoices, p.paymentAllocations, p.allItems)) return false;
    if (p.projFilter) {
      return it.project === p.projFilter || it.projectLabel === p.projFilter;
    }
    return true;
  });
  var grouped = groupItemsByPO(inProcess, p.allItems, p.purchaseOrders, p.invoices, p.paymentAllocations, p.projects);

  // Phase 5B (2026-06-24) — 1 FIN = 1 PO click-time gate. Per
  // ARCHITECTURE.md §4 + BILLING_REDESIGN_SPEC.md §5: items selected
  // for a FIN must all share a single PO. De-selection is never
  // blocked; first item picks the scope; subsequent additions must
  // match both project + PO.
  function selectedItemsArray(currentSet) {
    return inProcess.filter(function(it) { return currentSet.has(it.id); });
  }

  function toggleItem(id) {
    var next = new Set(p.selectedIds);
    if (next.has(id)) {
      // De-select branch — never blocked. Clear any prior gate error
      // so the user sees a clean state after they back off.
      next.delete(id);
      p.setErr('');
      p.setSelectedIds(next);
      return;
    }
    // Add branch — gate.
    var itemToAdd = inProcess.find(function(it) { return it.id === id; });
    var v = validateAddItemToFinSelection(itemToAdd, selectedItemsArray(next));
    if (!v.ok) {
      p.setErr(v.error);
      return;
    }
    next.add(id);
    p.setErr('');
    p.setSelectedIds(next);
  }

  function toggleGroup(groupKey) {
    var g = grouped.groups[groupKey];
    if (!g) return;
    var next = new Set(p.selectedIds);
    var allSel = g.items.every(function(it) { return next.has(it.id); });
    if (allSel) {
      // De-select the whole group — never blocked.
      g.items.forEach(function(it) { next.delete(it.id); });
      p.setErr('');
      p.setSelectedIds(next);
      return;
    }
    // Add path — validate each not-yet-selected item against the
    // running selection. Groups are PO-scoped by groupItemsByPO so a
    // mismatch surfaces on the first probe.
    var working = next;
    var workingItems = selectedItemsArray(working);
    for (var i = 0; i < g.items.length; i++) {
      var it = g.items[i];
      if (working.has(it.id)) continue;
      var v = validateAddItemToFinSelection(it, workingItems);
      if (!v.ok) { p.setErr(v.error); return; }
      working.add(it.id);
      workingItems.push(it);
    }
    p.setErr('');
    p.setSelectedIds(working);
  }

  async function generate() {
    p.setErr(''); p.setLoading(true);
    try {
      var selectedItems = inProcess.filter(function(it) { return p.selectedIds.has(it.id); });
      if (selectedItems.length === 0) {
        p.setErr('Select at least one item.');
        p.setLoading(false);
        return;
      }
      var projectLabel = selectedItems[0].projectLabel || selectedItems[0].project || '';
      var mixed = selectedItems.some(function(it) { return (it.projectLabel || it.project) !== projectLabel; });
      if (mixed) {
        p.setErr('All items in a single final invoice must be on the same project.');
        p.setLoading(false);
        return;
      }
      // Same-PO hard guard (2026-06-27, 1-PO-=-1-FIN enforcement). The
      // click-time validateAddItemToFinSelection in utils.js gates the
      // picker UI, but a programmatic / bulk path that bypasses the
      // picker (or a future API endpoint) could still bundle items from
      // multiple POs into one FIN. The save path mirrors the click-time
      // contract so the canonical model holds regardless of entry point.
      var firstPoNumber = selectedItems[0].poNumber || '';
      var mixedPo = selectedItems.some(function(it) { return (it.poNumber || '') !== firstPoNumber; });
      if (mixedPo) {
        var distinctPos = Array.from(new Set(selectedItems.map(function(it) { return it.poNumber || '(no PO)'; })));
        p.setErr('All items in a single final invoice must be on the same purchase order (1 FIN = 1 PO). '
               + 'Mixed POs in this selection: ' + distinctPos.join(', ') + '. '
               + 'Generate one FIN per PO.');
        p.setLoading(false);
        return;
      }

      // Merge pending FIN adjustments from POs whose items are on
      // this FIN. These were queued via the receipt verification
      // panel's "Bill as adjustment line" choice (Decision 2). User
      // can still edit/remove them in the preview overlay; on Save
      // & Commit they're cleared from the source POs.
      var selectedItemIdSet = new Set(selectedItems.map(function(it) { return it.id; }));
      var bundledPos = (p.purchaseOrders || []).filter(function(po) {
        if (!po) return false;
        var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
        return poItemIds.some(function(itemId) { return selectedItemIdSet.has(itemId); });
      });
      var pendingFromPos = bundledPos.reduce(function(acc, po) {
        return acc.concat((po.pendingFinAdjustments || []).map(function(adj) {
          return Object.assign({}, adj, { _sourcePoId: po.id });
        }));
      }, []);
      // User-added entries (manually typed in the editor) — kept across
      // regenerates. Distinguished from PO-queued entries by the absence
      // of _sourcePoId. PO entries refresh from the source on every
      // generate(); user entries persist verbatim.
      var userAdjustments = (adjustments || []).filter(function(a) { return !(a && a._sourcePoId); });
      var mergedAdjustments = pendingFromPos.concat(userAdjustments);
      // Bug B fix (2026-06-10): seed the adjustments editor state with
      // the merged list so PO-queued entries show up as editable rows.
      // Pre-fix, PO entries were only visible on the PDF preview iframe,
      // not in the editor — the bookkeeper couldn't edit/remove them
      // before save.
      setAdjustments(mergedAdjustments);

      // Deposit-payment dates for the PDF label (2026-09-03). Derived
      // from the selected items' DEP allocations; display-only.
      var depDatesFresh = finDepositPaymentHistory(
        { itemIds: selectedItems.map(function(it) { return it.id; }) },
        selectedItems, p.invoices, p.paymentAllocations || [], p.clientPayments || []
      ).map(function(r) { return fmtDate(r.date); }).filter(function(d, i, arr) { return d && d !== '—' && arr.indexOf(d) === i; });

      var built = buildFinalInvoicePayload({
        depositPaymentDates: depDatesFresh,
        items: selectedItems,
        allItems: p.allItems,
        allInvoices: p.invoices,
        // payment_allocations: required as of 2026-05-25 so the
        // balanceTotal computation can read actual cash received
        // against the deposit invoice (replaces the static 20%
        // formula). Without these, the builder treats the deposit
        // as fully unpaid — final invoice bills the full proposal
        // total.
        paymentAllocations: p.paymentAllocations || [],
        projects: p.projects,
        firmSettings: p.firmSettings,
        clients: p.clients,
        proposals: p.proposals,
        projectLabel: projectLabel,
        // CO + vendor-payment context — drives line-item rollups (CO
        // adjustments, credit-card processing fees passed through from
        // vendor_payments.cc_fee).
        changeOrders:    p.changeOrders    || [],
        vendorPayments:  p.vendorPayments  || [],
        purchaseOrders:  p.purchaseOrders  || [],
        // Phase 3 (2026-06-02): shadow-mode CC fee aggregation.
        // vendorPaymentItems is always passed so the legacy-vs-new
        // divergence check can run in the background. Flag controls
        // which path renders (off = legacy, on = new). Default off
        // until Cimon flips VITE_FEATURE_VENDOR_PAYMENT_ITEMS_CC_FEE_READS
        // in Vercel after the 7-day-clean criterion is met.
        vendorPaymentItems: p.vendorPaymentItems || [],
        usePerItemCcFee:    String(import.meta.env.VITE_FEATURE_VENDOR_PAYMENT_ITEMS_CC_FEE_READS || '') === 'true',
        // Phase 5C (2026-06-24): fee_share_* primary read path. Shadow
        // mode by default — buildFinalInvoicePayload computes both
        // legacy items.fees and new fee_share paths, logs divergence
        // via console.warn + onFeeShareDivergence. Flip
        // VITE_FEATURE_FEE_SHARE_PRIMARY_READS=true in Vercel after the
        // 1-2-week clean-shadow criterion is met across Hoback /
        // Ptarmigan / Moskito. Currently ships freight + tariff only;
        // crating / installation / custom remain on the legacy path
        // until their respective phases land.
        usePerItemFeeShares: String(import.meta.env.VITE_FEATURE_FEE_SHARE_PRIMARY_READS || '') === 'true',
        // Pass-through adjustments edited in the preview overlay
        // (2026-06-06 patch) PLUS pending entries pulled from each
        // bundled PO's pendingFinAdjustments queue (2026-06-10
        // verification-panel "Bill as adjustment line" flow). The
        // PO entries always come first so duplicates the user adds
        // manually go below them in the FIN PDF ordering.
        adjustments:        mergedAdjustments,
        // Route divergence into Sentry as a 'warning' event with the
        // full record under `extra`. console.warn still fires in
        // utils.js for live debugging; Sentry is the centralized
        // monitoring path that gates the 7-day-clean cutover criterion.
        onDivergence: function(record) {
          try {
            Sentry.captureMessage('[phase-3-cc-fee-divergence]', {
              level: 'warning',
              extra: record,
            });
          } catch (_e) {}
        },
        // Phase 5C divergence hook — same pattern as CC fee.
        onFeeShareDivergence: function(record) {
          try {
            Sentry.captureMessage('[phase-5c-fee-share-divergence]', {
              level: 'warning',
              extra: record,
            });
          } catch (_e) {}
        },
      });

      // Render the preview PDF. The overlay below keys on p.pdfUrl, so
      // without this fetch Generate silently does nothing (the QuickBooks
      // removal in b7f32f8 deleted it along with the QB guard beside it).
      var res = await fetch('/api/generate-deposit-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(built.payload),
      });
      if (!res.ok) {
        var err = await res.json().catch(function() { return {}; });
        throw new Error(err.error || 'PDF generation failed');
      }
      var blob = await res.blob();
      if (p.pdfUrl) URL.revokeObjectURL(p.pdfUrl);
      p.setPdfUrl(URL.createObjectURL(blob));
      p.setPdfFilename(built.invoiceRef.replace(/[^a-zA-Z0-9-]/g, '') + '.pdf');

      var invoiceId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID() : (Date.now() + '-' + Math.random());
      // PRE-COMMIT DRAFT (2026-05-25): the in-memory invoice record is
      // assembled here but NOT persisted. We stash it in pendingInvoice
      // state so the PDF preview can surface alongside Save & Commit /
      // Cancel buttons. finalizeInvoiceAtomic only fires when the user
      // clicks Save & Commit (see saveAndCommit below). Same atomic
      // RPC, same record shape — only the click sequence changed.
      //
      // Why: pre-2026-05-25 the commit fired on PDF render, so the
      // preview was always post-commit. The bookkeeper saw the PDF
      // but had no path to verify-and-back-out if a fee or item line
      // looked wrong. Deletion afterward was guarded + didn't unwind
      // item/CO stamps (see FOLLOWUPS.md "Safe delete / revise").
      //
      // Invariant preserved: the invoice record built here is the
      // exact record that persists if Save fires (no rebuild on Save).
      // The PDF the bookkeeper reviews matches the persisted snapshot.
      if (!p.finalizeInvoiceAtomic) {
        p.setErr('finalizeInvoiceAtomic missing from context — cannot generate final invoice safely.');
        p.setLoading(false);
        return;
      }
      var invoice = {
        id: invoiceId,
        invoiceRef: built.invoiceRef,
        invoiceType: 'final',
        status: 'draft',
        proposalId: built.proposalId || '',
        project: projectLabel,
        clientId: built.clientId,
        clientName: built.clientName,
        itemIds: built.itemIds,
        lineItems: built.payload.lineItems,
        productTotal: built.productTotalRounded,
        feesTotal: built.feesTotalRounded,
        // Per-fee breakdown snapshot at FIN-generation time. Mirrors the
        // deposit-invoice immutability invariant: regenerating the FIN PDF
        // reads from these stored values rather than recomputing per-fee
        // dollars from current item state. Without this snapshot a paid
        // FIN's regen would silently collapse to a single "Fees & Sales
        // Tax" lumped line (per the hasPerFeeBreakdown fallback in the
        // regen path). Tax obligation crystallizes at the dollar amount
        // billed; this row IS the snapshot.
        feeFreightTot:      built.payload.feeFreightTot      || 0,
        feeCratingTot:      built.payload.feeCratingTot      || 0,
        feeInstallationTot: built.payload.feeInstallationTot || 0,
        feeTariffTot:       built.payload.feeTariffTot       || 0,
        feeSalesTaxTot:     built.payload.feeSalesTaxTot     || 0,
        // Card fee (2026-09-07). Persisted so invoiceCcFeeTot reads the
        // stored number instead of back-deriving it, and so the sales tax
        // frozen in feeSalesTaxTot has its fee frozen beside it.
        feeCcProcessingTot: built.payload.feeCcProcessingTot || 0,
        feeCustom:          built.payload.feeCustom          || [],
        // Markup dollars (2026-09-08 columns). The payload computed these
        // from day one but this record never copied them, so every FIN
        // stored NULL and Reconciliation derived margin live for all of
        // them — the write that never ran (found 2026-10-06, same class as
        // the fee_share bug). Stored = what this FIN billed, frozen.
        markupProductTot:   built.payload.markupProductTot != null ? built.payload.markupProductTot : null,
        markupFeesTot:      built.payload.markupFeesTot    != null ? built.payload.markupFeesTot    : null,
        // Adjustments persist on the invoice row so the regenerate-from-
        // saved PDF rebuilds the same shape (2026-06-06 patch).
        adjustments:        built.adjustments                || [],
        subtotal: built.subtotal,
        amountDue: built.amountDue,
        notes: '',
        pdfStoragePath: '',
        sentAt: '',
        createdBy: p.userEmail || '',
      };
      var coIds = built.coIdsConsumed || [];
      // Card-fee rows this FIN bills. Stamped at Save & Commit so the
      // same fee can never ride a second invoice (split shipment), and so
      // each bank fee names the client invoice that recovered it.
      var ccFeeIds = built.ccFeeIdsConsumed || [];
      if (p.setPendingInvoice) {
        p.setPendingInvoice({
          invoice: invoice, itemIds: built.itemIds, coIds: coIds, ccFeeIds: ccFeeIds,
          // Per-item tax snapshot — frozen into final_invoice_items at commit
          // so the Sales Tax report files what this document billed.
          taxRows: built.taxSnapshot || [],
        });
      }
      p.setLoading(false);
    } catch (e) {
      p.setErr(e.message || 'Failed to generate final invoice');
      p.setLoading(false);
    }
  }

  // Save & Commit — fires only when the bookkeeper clicks the button
  // in the preview overlay. Reads the in-memory pendingInvoice (built
  // during generate() above) and persists via finalizeInvoiceAtomic.
  // On success: clears pendingInvoice + preview state + selection. On
  // failure: leaves pendingInvoice + preview in place so the user can
  // retry without losing the draft.
  async function saveAndCommit() {
    if (!p.pendingInvoice || !p.finalizeInvoiceAtomic) return;
    if (p.saving) return; // idempotent double-click guard

    // Receipt-status pre-flight (2026-06-13). When a FIN's PO(s) have
    // no approved vendor receipt, vendor-side fees that weren't on the
    // proposal (install, freight, customs) have no path into the bill
    // once this FIN is committed — the fee verification panel never
    // runs because the receipts haven't been processed yet. Soft
    // warning; the bookkeeper can confirm to proceed (some POs are
    // legitimately invoiced before the vendor final lands).
    //
    // 2026-06-26 revert: the duplicate-FIN guard that briefly lived
    // here is removed. It was introduced under the assumption that
    // auto-created stubs would be the canonical FIN; in practice the
    // stubs are empty placeholders and the Open-tab Save & Commit
    // remains the only working composition surface. The guard was
    // blocking Cimon's entire billing pipeline for every PO that had
    // a backfilled or auto-created stub. Restored to the original
    // pre-Commit-4-followup flow. Stubs become data debt the
    // bookkeeper deletes / supersedes manually; the architectural
    // path (Option 2 — Drafts row navigates to Open + consumes the
    // stub on commit) ships separately.
    var pi = p.pendingInvoice;
    var receiptStatus = finReceiptStatus(pi.invoice, p.allItems || [], p.purchaseOrders || [], p.receipts || []);
    if (receiptStatus.posWithoutReceipts.length > 0) {
      var missing = receiptStatus.posWithoutReceipts.map(function(po) {
        return po.poRef + (po.vendorName ? ' (' + po.vendorName + ')' : '');
      }).join(', ');
      var proceed = window.confirm(
        'Heads up — no approved vendor receipts found for the following PO(s):\n\n' +
        missing + '\n\n' +
        'Finalizing this client invoice before the vendor receipt is uploaded means any vendor-side fees (install, freight, customs) that weren\u2019t on the original proposal won\u2019t be billable through the standard fee-verification flow once the FIN closes paid.\n\n' +
        'Continue anyway?'
      );
      if (!proceed) return;
    }

    p.setSaving(true); p.setErr('');
    try {
      var atomicResult = await p.finalizeInvoiceAtomic(
        pi.invoice, pi.itemIds, pi.coIds, pi.ccFeeIds || [], pi.taxRows || []);
      if (!atomicResult || !atomicResult.ok) {
        p.setErr('Final invoice not saved: ' +
          ((atomicResult && atomicResult.error) || 'unknown error') +
          '. Database state is unchanged. Retry, or click Cancel to discard.');
        p.setSaving(false);
        return;
      }
      // Commit landed. Archive the PDF blob BEFORE revoking the
      // object URL — the upload helper fetches the blob via the URL
      // and uploads to Supabase Storage. Best-effort: failure is
      // logged but doesn't block the commit (the invoice row already
      // persisted via finalizeInvoiceAtomic).
      if (p.pdfUrl && pi.invoice && pi.invoice.id) {
        try {
          var blobForArchive = await fetch(p.pdfUrl).then(function(res) { return res.blob(); });
          var archivedPath = await uploadFinPdfArchive(pi.invoice.id, blobForArchive);
          // Refresh local state so the InvoiceDetail's "View Original"
          // button shows the storage path without a full reload.
          if (archivedPath && p.setInvoices) {
            p.setInvoices(function(prev) {
              return (prev || []).map(function(inv) {
                return inv.id === pi.invoice.id
                  ? Object.assign({}, inv, { pdfStoragePath: archivedPath })
                  : inv;
              });
            });
          }
        } catch (archiveErr) {
          // Don't surface to the bookkeeper — the FIN saved fine; the
          // PDF archive is a secondary artifact. Logged for follow-up.
          console.warn('[fin-pdf-archive] non-fatal failure:', archiveErr);
        }
      }
      // Clear consumed pendingFinAdjustments from the source POs.
      // The user-edited `adjustments` state holds the version that
      // landed on this FIN (post-merge). Each PO's queue is cleared
      // (we consumed everything that was pending). 2026-06-10.
      try {
        var consumedPoIds = new Set();
        (pi.invoice && Array.isArray(pi.invoice.adjustments) ? pi.invoice.adjustments : []).forEach(function(adj) {
          if (adj && adj._sourcePoId) consumedPoIds.add(adj._sourcePoId);
        });
        if (consumedPoIds.size > 0 && p.savePOToDb && p.setPurchaseOrders) {
          var posToUpdate = (p.purchaseOrders || []).filter(function(po) { return consumedPoIds.has(po.id); });
          for (var pi2 = 0; pi2 < posToUpdate.length; pi2++) {
            var poRow = posToUpdate[pi2];
            var patched = Object.assign({}, poRow, { pendingFinAdjustments: [] });
            p.setPurchaseOrders(function(prev) {
              return (prev || []).map(function(x) { return x && x.id === patched.id ? patched : x; });
            });
            try { await p.savePOToDb(patched); }
            catch (poErr) { console.error('[fin-finalize] clearing po.pendingFinAdjustments failed for', patched.id, poErr); }
          }
        }
      } catch (clearErr) {
        console.warn('[fin-finalize] non-fatal — clearing pending adjustments threw:', clearErr);
      }
      if (p.pdfUrl) URL.revokeObjectURL(p.pdfUrl);
      p.setPdfUrl(null);
      p.setPdfFilename('');
      p.setPendingInvoice(null);
      p.setSelectedIds(new Set());
      setAdjustments([]); // reset Adjustments editor state for next FIN
      p.setSaving(false);
    } catch (e) {
      p.setErr(e.message || 'Save failed');
      p.setSaving(false);
    }
  }

  // Cancel — discard the pending invoice + preview. No DB writes ever
  // happened (this is the whole point of the pre-commit flow).
  function cancelPreview() {
    if (p.saving) return; // prevent cancel during in-flight commit
    if (p.pdfUrl) URL.revokeObjectURL(p.pdfUrl);
    p.setPdfUrl(null);
    p.setPdfFilename('');
    p.setPendingInvoice(null);
    // Keep selectedIds intact — the bookkeeper may want to tweak the
    // selection and re-generate without re-checking from scratch.
  }

  // Per-item project lookup so estimates carry the tax config — the
  // same Model B numbers the FIN generator will bill (2026-08-15).
  function estWithProject(it) {
    var projObj = (p.projects || []).find(function(pr) {
      return pr && pr.label === (it.project || it.projectLabel || '');
    });
    return estimatedFinalInvoiceAmount(it, p.allItems, p.invoices, p.paymentAllocations, projObj);
  }
  var totalEstimated = inProcess.reduce(function(s, it) { return s + estWithProject(it); }, 0);
  var selectedTotal = inProcess
    .filter(function(it) { return p.selectedIds.has(it.id); })
    .reduce(function(s, it) { return s + estWithProject(it); }, 0);

  return (
    <div style={{ padding: '16px 18px', flex: 1, overflowY: 'auto' }}>

      {/* Final-invoice PDF preview overlay — PRE-COMMIT DRAFT.
          The invoice exists in p.pendingInvoice (in-memory only) at this
          point. Save & Commit fires finalizeInvoiceAtomic. Cancel just
          drops the draft — no DB writes ever happened. Download is a
          convenience for the bookkeeper to grab the PDF before saving
          (in case they want to preview offline + decide later — they'd
          come back and re-generate). */}
      {p.pdfUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#1a1a1a' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{p.pdfFilename || 'Final Invoice'}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#BF6800',
                textTransform: 'uppercase', letterSpacing: '0.07em',
                padding: '2px 6px', border: '0.5px solid #BF6800', borderRadius: 3 }}>
                Draft — not saved
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <a href={p.pdfUrl} download={p.pdfFilename}
                style={{ padding: '6px 16px', background: 'none', border: '1px solid #555', color: '#fff', borderRadius: 6, fontSize: 12, cursor: 'pointer', textDecoration: 'none' }}>
                Download PDF
              </a>
              <button onClick={cancelPreview}
                disabled={!!p.saving}
                style={{ padding: '6px 16px', background: 'none', border: '1px solid #888', color: '#fff', borderRadius: 6, fontSize: 12,
                  cursor: p.saving ? 'default' : 'pointer', opacity: p.saving ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={saveAndCommit}
                disabled={!!p.saving || !p.pendingInvoice}
                style={{ padding: '6px 18px',
                  background: p.saving ? '#666' : '#fff',
                  color: p.saving ? '#aaa' : '#1a1a1a',
                  border: 'none', borderRadius: 6,
                  fontSize: 12, fontWeight: 600,
                  cursor: p.saving ? 'default' : 'pointer' }}>
                {p.saving ? 'Saving\u2026' : 'Save & Commit'}
              </button>
            </div>
          </div>
          {p.err && (
            <div style={{ padding: '8px 16px', background: '#5a1f1f', color: '#FFB4A8',
              fontSize: 11, borderBottom: '1px solid #333' }}>
              {p.err}
            </div>
          )}
          {/* Adjustments editor (2026-06-06 patch) — pass-through deltas
              layered onto the FIN at generation time. Click Regenerate
              after editing to rebuild the PDF preview with the new shape.
              Survives Cancel so the user can iterate; resets on Save. */}
          <div style={{ padding: '10px 16px 12px', background: '#1a1a1a', borderBottom: '1px solid #333',
            maxHeight: 220, overflowY: 'auto', color: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Adjustments {adjustments.length > 0 ? '(' + adjustments.length + ')' : ''}
              </span>
              <span style={{ fontSize: 10, color: '#888' }}>
                Late tariff, freight overage, crating overage — taxable rows recompute sales tax.
              </span>
              <div style={{ flex: 1 }} />
              <button onClick={addAdjustment}
                style={{ padding: '4px 10px', background: 'transparent', border: '0.5px solid #555',
                  color: '#fff', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
                + Add adjustment
              </button>
              {adjustments.length > 0 && (
                <button onClick={generate} disabled={p.loading}
                  style={{ padding: '4px 12px', background: p.loading ? '#444' : '#fff',
                    color: p.loading ? '#888' : '#111', border: 'none', borderRadius: 4,
                    fontSize: 11, fontWeight: 600, cursor: p.loading ? 'default' : 'pointer' }}>
                  {p.loading ? 'Regenerating…' : 'Regenerate Preview'}
                </button>
              )}
            </div>
            {adjustments.length === 0 && (
              <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                No adjustments. Add one to layer a pass-through delta onto this FIN.
              </div>
            )}
            {adjustments.map(function(adj, idx) {
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input
                    type="text"
                    placeholder="Description (e.g., Customs duty revision)"
                    value={adj.description}
                    onChange={function(e) { updateAdjustment(idx, { description: e.target.value }); }}
                    style={{ flex: 1, padding: '4px 8px', background: '#2a2a2a', border: '0.5px solid #444',
                      borderRadius: 4, color: '#fff', fontSize: 12, fontFamily: 'inherit' }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={adj.amount}
                    onChange={function(e) { updateAdjustment(idx, { amount: parseFloat(e.target.value) || 0 }); }}
                    style={{ width: 100, padding: '4px 8px', background: '#2a2a2a', border: '0.5px solid #444',
                      borderRadius: 4, color: '#fff', fontSize: 12, fontFamily: 'inherit', textAlign: 'right' }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#bbb', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={!!adj.taxable}
                      onChange={function(e) { updateAdjustment(idx, { taxable: e.target.checked }); }}
                    />
                    Taxable
                  </label>
                  <button onClick={function() { removeAdjustment(idx); }}
                    style={{ padding: '4px 8px', background: 'transparent', border: '0.5px solid #555',
                      color: '#FFB4A8', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
          <iframe src={p.pdfUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="Final Invoice Preview" />
        </div>
      )}

      {/* Summary line. Internal project select hidden when the outer
          FIN-section project buttons drive (2026-06-07 redesign) so
          the project lens isn't duplicated in two places. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#555552' }}>
          <strong style={{ color: '#111111', fontWeight: 500 }}>{inProcess.length}</strong> items in process · est. {fmt(totalEstimated)}
        </span>
        <div style={{ flex: 1 }} />
        {!p.hideProjectSelect && (
          <select value={p.projFilter} onChange={function(e) { p.setProjFilter(e.target.value); }}
            style={{ padding: '6px 10px', border: '0.5px solid #E0E0DC', borderRadius: 5, fontSize: 11, background: '#FFFFFF', fontFamily: 'inherit', color: '#111111' }}>
            <option value="">All Projects</option>
            {(p.projects || []).map(function(proj) { return <option key={proj.id} value={proj.label}>{proj.label}</option>; })}
          </select>
        )}
      </div>

      {/* Sticky selection bar — design matches the rest of the surface
          (white ground, hairline border) per Cimon's design language.
          The fuchsia left-edge accent on selected rows below is the
          visual punch; this bar just surfaces N selected + Generate. */}
      {p.selectedIds.size > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 5, marginBottom: 14, padding: '10px 14px', background: '#FFFFFF', color: '#111111', border: '0.5px solid #E0E0DC', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 500, color: '#111111' }}>{p.selectedIds.size} item{p.selectedIds.size === 1 ? '' : 's'} selected</span>
          <span style={{ fontSize: 11, color: '#888880' }}>· est. {fmt(selectedTotal)}</span>
          <div style={{ flex: 1 }} />
          <button onClick={function() { p.setSelectedIds(new Set()); }}
            style={{ padding: '6px 12px', background: 'transparent', border: '0.5px solid #E0E0DC', borderRadius: 5, color: '#555552', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
            Clear
          </button>
          <button onClick={generate} disabled={p.loading}
            style={{ padding: '6px 14px', background: p.loading ? '#888880' : '#111111', color: '#FFFFFF', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 500, cursor: p.loading ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {p.loading ? 'Generating…' : 'Generate Final Invoice'}
          </button>
        </div>
      )}

      {/* Card fees on this invoice (2026-09-07). The fee rides inside the
          client's Shipping & Handling line with no label of its own, so
          this is the one place the operator sees it BEFORE generating:
          every captured fee, the date off its own receipt, and the sales
          tax that will be charged on it. Read-only — the numbers come
          from the receipts, which are the paper of record. */}
      {p.selectedIds.size > 0 && (function() {
        var sel = inProcess.filter(function(it) { return p.selectedIds.has(it.id); });
        var sources = ccFeeSources({
          items:          sel,
          vendorPayments: p.vendorPayments || [],
          purchaseOrders: p.purchaseOrders || [],
          receipts:       p.receipts       || [],
        });
        // Suppression: ONE item carrying a retired ccFeePct zeroes the
        // receipt-side fee for the WHOLE invoice, so a captured fee can
        // be entirely real and still not billed. The operator has to see
        // that, or the money silently goes unrecovered.
        var suppressedBy = sel.filter(function(it) { return (parseFloat(it.ccFeePct) || 0) > 0; });
        if (!sources.length && !suppressedBy.length) return null;

        // Already recovered by an earlier invoice — shown, but not
        // charged again. This is the split-shipment case: a second FIN on
        // the same PO used to sweep up the whole fee history.
        var alreadyBilled = sources.filter(function(r) { return !!r.billedInvoiceId; });
        var open          = sources.filter(function(r) { return !r.billedInvoiceId; });
        var captured = Math.round(open.reduce(function(s2, r) { return s2 + r.amount; }, 0) * 100) / 100;
        var billed   = suppressedBy.length ? 0 : captured;
        var invRefById = {};
        (p.invoices || []).forEach(function(iv) {
          if (iv && iv.id != null) invRefById[String(iv.id)] = iv.invoiceRef || '';
        });
        var projObj  = (p.projects || []).find(function(pr) {
          return pr && pr.label === ((sel[0] && (sel[0].project || sel[0].projectLabel)) || '');
        });
        var taxable  = !!projObj && hasProjectTaxConfig(projObj) && projObj.taxesMerchandise !== false;
        var ccTax    = taxable
          ? Math.round(billed * (parseFloat(projObj.taxRate) || 0) * 100) / 100
          : 0;

        var LBL = { fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' };
        var ROW = { display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0' };

        return (
          <div data-testid="fin-cc-fees" style={{
            marginBottom: 14, padding: '10px 14px', background: '#FFFFFF',
            border: '0.5px solid #E0E0DC', borderRadius: 6,
          }}>
            <div style={Object.assign({}, LBL, { marginBottom: 6 })}>Card fees captured</div>

            {sources.map(function(r, i) {
              var spent = !!r.billedInvoiceId;
              return (
                <div key={r.paymentId || i} style={ROW}>
                  <span style={{ fontSize: 11, color: spent ? '#888880' : '#111111', minWidth: 74 }}>
                    {r.date ? fmtDate(r.date) : '—'}
                  </span>
                  <span style={{ fontSize: 11, color: spent ? '#888880' : '#555552' }}>{r.vendorName || '—'}</span>
                  <span style={{ fontSize: 10, color: '#888880' }}>
                    {r.poRef}{r.dateFrom === 'receipt' ? ' · receipt' : r.dateFrom === 'payment' ? ' · payment entry' : ''}
                    {spent ? ' · billed on ' + (invRefById[String(r.billedInvoiceId)] || 'an earlier invoice') : ''}
                  </span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: spent ? '#888880' : '#111111' }}>{fmt(r.amount)}</span>
                </div>
              );
            })}

            <div style={{ borderTop: '0.5px solid #E0E0DC', marginTop: 6, paddingTop: 6 }}>
              <div style={ROW}>
                <span style={{ fontSize: 11, color: '#555552' }}>Billed inside Shipping &amp; Handling</span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11, fontWeight: 500, color: '#111111' }}>{fmt(billed)}</span>
              </div>
              <div style={ROW}>
                <span style={{ fontSize: 11, color: '#555552' }}>
                  Sales tax on card fee{taxable ? '' : ' — project has no tax rate'}
                </span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: '#111111' }}>{fmt(ccTax)}</span>
              </div>
            </div>

            {suppressedBy.length > 0 && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: '0.5px solid #E0E0DC',
                            fontSize: 11, color: '#D4007A' }}>
                Not billed — {suppressedBy.map(function(it) { return it.itemNumber || it.productName || it.item; }).join(', ')} carries a retired CC % that already recovered it
              </div>
            )}
          </div>
        );
      })()}

      {p.err && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FDECEA', border: '0.5px solid #F2A39C', color: '#C8322A', fontSize: 11, borderRadius: 4 }}>
          {p.err}
        </div>
      )}

      {/* Grouped list — empty state matches InvoiceTable's dashed-border
          empty state used on the Drafts/Ready/Sent tabs. */}
      {inProcess.length === 0 && (
        <div style={{ padding: '24px 0', color: '#BBBBBB', fontSize: 12, fontStyle: 'italic', textAlign: 'center', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
          Nothing in flight. Items appear here after Client Approved + Deposit Paid.
        </div>
      )}

      {/* ── Grouped table (2026-07-05 redesign, mockup approved by Cimon) ──
          Items-page table grammar: one table, a PO header row per group,
          item rows indented beneath. Every money column NAMES ITS SIDE —
          client or vendor — per the standing rule ("anything client
          related should say 'client'"). The Vendor payment column reads
          the per-item vendor ledger via computePoItemPaymentHistory
          (vendor_payment_items shares — the same source as the
          Live-annotated PO view), so vendor money is per ITEM, not just
          the PO-level pill. */}
      {inProcess.length > 0 && (
      <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 6, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[
                ['', 30, 'left'],
                ['Item', null, 'left'],
                ['Item status', null, 'left'],
                ['Vendor payment', null, 'left'],
                ['Client deposit paid', null, 'right'],
                ['Client final due', null, 'right'],
                ['Client fees', null, 'right'],
                ['Est. client invoice', null, 'right'],
              ].map(function(col, i) {
                return (
                  <th key={i} style={{
                    textAlign: col[2], width: col[1] || undefined,
                    fontSize: 9.5, letterSpacing: '0.07em', textTransform: 'uppercase',
                    color: '#888880', fontWeight: 500, padding: '8px 10px',
                    borderBottom: '0.5px solid #E0E0DC', background: '#FFFFFF', whiteSpace: 'nowrap',
                  }}>{col[0]}</th>
                );
              })}
            </tr>
          </thead>
          <tbody>
      {grouped.orderedKeys.map(function(key) {
        var g = grouped.groups[key];
        var allChecked = g.items.every(function(it) { return p.selectedIds.has(it.id); });
        var someChecked = g.items.some(function(it) { return p.selectedIds.has(it.id); });
        // Resolve the PO object so we can call isPoFullyPaid against it.
        // No-PO groups never get a vendor-payment pill.
        var poObj = g.poId ? (p.purchaseOrders || []).find(function(po) { return po.id === g.poId; }) : null;
        var paidStatus = poObj
          ? isPoFullyPaid(poObj, p.allItems, p.vendorPayments || [], p.vendorPaymentItems || [], p.vendorCreditLedger || [])
          : { isFullyPaid: false };
        var readyToInvoice = !!paidStatus.isFullyPaid;
        // Per-item vendor payment shares (vendor_payment_items) — one
        // resolution per group, read per row below.
        var poHistory = poObj
          ? computePoItemPaymentHistory(poObj, p.allItems, p.vendorPayments || [], p.vendorPaymentItems || [], p.vendors || [])
          : {};
        // PO header pill — three states, each naming what it measures:
        //   green   — vendor money closed AND nothing left to bill the
        //             client (100%-upfront POs; the FIN would be a $0
        //             closing record)
        //   fuchsia — vendor money closed and a client balance exists:
        //             the actionable "generate the FIN" cue
        //   muted   — vendor payments still open: the running tally
        var pill = null;
        if (readyToInvoice) {
          pill = g.estimatedTotal > 0.005
            ? { color: '#D4007A', testid: 'po-ready-to-invoice-badge', label: 'Vendor paid in full · ready to invoice' }
            : { color: '#1A5C38', testid: 'po-vendor-paid-nothing-to-bill-badge', label: 'Vendor paid in full · nothing left to bill' };
        } else if (poObj && paidStatus.expectedTotal > 0) {
          pill = { color: '#888880', testid: 'po-vendor-balance-open-badge',
                   label: 'Vendor: ' + fmt(paidStatus.paidTotal || 0) + ' of ' + fmt(paidStatus.expectedTotal) + ' paid' };
        }
        var headerCells = [
          <td key="cb" style={{ padding: '10px 10px', borderBottom: '0.5px solid #EFEFEC', borderTop: '0.5px solid #E0E0DC', verticalAlign: 'top' }}>
            <input type="checkbox"
              checked={allChecked}
              ref={function(el) { if (el) el.indeterminate = !allChecked && someChecked; }}
              onChange={function() { toggleGroup(key); }}
              style={{ cursor: 'pointer' }} />
          </td>,
          <td key="id" colSpan={3} style={{ padding: '10px 10px', borderBottom: '0.5px solid #EFEFEC', borderTop: '0.5px solid #E0E0DC' }}>
            {/* PO identity reads a step above the item rows (Cimon
                2026-07-05: "slightly bigger and slightly bolder"). */}
            <span style={{ fontSize: 12.5, fontWeight: 600, fontFamily: g.poRef ? 'monospace' : 'inherit', color: '#111111' }}>
              {g.poRef || (p.projFilter ? 'No PO' : (g.project + ' (no PO)'))}
            </span>
            {pill && (
              <span data-testid={pill.testid}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 8, padding: '1px 8px', border: '0.5px solid ' + pill.color, borderRadius: 10, fontSize: 9, color: pill.color, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', fontFamily: 'inherit', background: '#FFFFFF', whiteSpace: 'nowrap' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: pill.color }} />
                {pill.label}
              </span>
            )}
            <div style={{ fontSize: 10.5, color: '#888880', marginTop: 2 }}>
              {g.vendor} · {g.items.length} item{g.items.length === 1 ? '' : 's'}
            </div>
          </td>,
          <td key="sp" colSpan={3} style={{ borderBottom: '0.5px solid #EFEFEC', borderTop: '0.5px solid #E0E0DC' }} />,
          <td key="est" style={{ padding: '10px 10px', borderBottom: '0.5px solid #EFEFEC', borderTop: '0.5px solid #E0E0DC', textAlign: 'right', verticalAlign: 'top' }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: g.estimatedTotal > 0.005 ? '#111111' : '#888880', fontFeatureSettings: '"tnum", "lnum"' }}>{fmt(g.estimatedTotal)}</div>
            <div style={{ fontSize: 9, color: '#888880', marginTop: 1 }}>group est.</div>
          </td>,
        ];
        return (
          <React.Fragment key={key}>
            <tr>{headerCells}</tr>
            {g.items.map(function(it) {
                var checked = p.selectedIds.has(it.id);
                // Readiness = the vendor has committed — status advanced
                // OR principal vendor money on the PO, deposit included
                // (Cimon 2026-07-05: "any time we give money to the
                // vendor, the order is considered committed"). The rule
                // lives in isItemReadyForFinalInvoice (utils) — one
                // source of truth with the ItemDatabase BILL READY badge
                // + the ItemForm banner. statusReady is derived
                // separately only so the subline can name WHICH evidence
                // made the row ready.
                var statusReady = isItemReadyForFinalInvoice(it, p.invoices, p.paymentAllocations, p.allItems);
                var vendorMoneyOnPo = poHasVendorPrincipalPayment(poObj, p.vendorPayments || []);
                var ready = statusReady || readyToInvoice || vendorMoneyOnPo;
                // Per-row client-money breakdown (2026-06-07, relabeled
                // 2026-07-05 — every column names its side). Math:
                //   client deposit paid = itemPaidShare (cash recorded
                //     against the item's DEP invoice; Layer 1/2/3)
                //   client final due = lifecycleOwed − depositPaid − fees
                //   client fees = marked-up item.fees that would land on
                //     the FIN (vdp<100 only; vdp=100 collected at deposit)
                var projObj = (p.projects || []).find(function(pr) {
                  return pr.label === (it.project || it.projectLabel || '');
                });
                var est = estimatedFinalInvoiceAmount(it, p.allItems, p.invoices, p.paymentAllocations, projObj);
                var depositPaid = itemPaidShare(it, p.invoices, p.paymentAllocations, p.allItems || [it]);
                var lifecycleOwed = itemLifecycleOwed(it, p.allItems || [it], projObj);
                var vdp = parseFloat(it.depositPct || 50);
                var feesTot = 0;
                if (vdp < 100) {
                  var fees = it.fees || {};
                  function nfn(v) { var x = parseFloat(String(v || '').replace(/[$,]/g, '')); return isNaN(x) ? 0 : x; }
                  if (nfn(fees.freight)      > 0) feesTot += applyFreightMarkup(nfn(fees.freight), it);
                  if (nfn(fees.crating)      > 0) feesTot += applyFreightMarkup(nfn(fees.crating), it);
                  if (nfn(fees.installation) > 0) feesTot += applyFreightMarkup(nfn(fees.installation), it);
                  if (nfn(fees.tariff)       > 0) feesTot += applyTaxMarkup(nfn(fees.tariff));
                  if (nfn(fees.salesTax)     > 0) feesTot += applyTaxMarkup(nfn(fees.salesTax));
                  (fees.custom || []).forEach(function(c) { if (nfn(c.amount) > 0) feesTot += customFeeClient(c, it); });
                }
                var finalDueBase = Math.max(0, lifecycleOwed - depositPaid - feesTot);
                // Sub-2¢ residue is per-item deposit rounding, not money
                // owed — same clamp as estimatedFinalInvoiceAmount and the
                // FIN payload's per-item balance (2026-07-05).
                if (finalDueBase < 0.02) finalDueBase = 0;
                var feesRounded = Math.round(feesTot * 100) / 100;
                // Vendor payment cell — this item's share of what the firm
                // has actually paid the vendor (per-item vendor ledger),
                // against its net cost basis. No shares recorded → an
                // honest dash, never a fabricated number.
                var hist = poHistory[it.id];
                var vendorNet = Math.round(itemNet(it) * 100) / 100;
                var vendorPaid = hist && hist.hasAnyPayment ? hist.totalPaid : 0;
                var vendorState = !hist || !hist.hasAnyPayment
                  ? 'none'
                  : (vendorPaid >= vendorNet - Math.max(1, vendorNet * 0.005) ? 'paid' : 'partial');
                var tdBase = { padding: '9px 10px', fontSize: 11, borderBottom: '0.5px solid #EFEFEC', verticalAlign: 'top', background: '#FFFFFF' };
                var moneyCell = function(v, opts) {
                  var o = opts || {};
                  return Object.assign({}, tdBase, {
                    textAlign: 'right', whiteSpace: 'nowrap', fontFeatureSettings: '"tnum", "lnum"',
                    color: v > 0.005 ? (o.green ? '#1A5C38' : '#111111') : '#888880',
                    fontWeight: v > 0.005 && !o.plain ? 500 : 400,
                  });
                };
                return (
                  <tr key={it.id} onClick={function() { toggleItem(it.id); }} style={{ cursor: 'pointer' }}>
                    <td style={Object.assign({}, tdBase, { paddingLeft: 26, boxShadow: checked ? 'inset 2px 0 0 #D4007A' : 'none' })}>
                      <input type="checkbox" checked={checked}
                        onClick={function(e) { e.stopPropagation(); }}
                        onChange={function() { toggleItem(it.id); }} />
                    </td>
                    <td style={Object.assign({}, tdBase, { paddingLeft: 14 })}>
                      <div style={{ fontWeight: 500, color: '#111111' }}>{it.item || it.productName || 'Untitled'}</div>
                      {it.itemNumber && <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#888880', marginTop: 2 }}>{it.itemNumber}</div>}
                    </td>
                    <td style={tdBase}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', color: '#555552' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ready ? '#1A5C38' : '#F5C800', flexShrink: 0 }} />
                        {it.status}
                      </span>
                      {/* Readiness speaks both ways: ready rows say so;
                          not-ready rows name the ACTUAL boundary
                          (isItemReadyForFinalInvoice: ready = Order
                          Confirmed / Received / Installed — vendor
                          confirmation, not goods arrival; 2026-07-05). */}
                      {ready ? (
                        <div style={{ fontSize: 9.5, color: '#1A5C38', marginTop: 2 }}>
                          {statusReady
                            ? (it.status === 'Ordered' ? 'ready — final numbers in' : 'ready')
                            : (readyToInvoice
                              ? 'ready — vendor paid in full'
                              : 'ready — vendor committed (deposit paid)')}
                        </div>
                      ) : (
                        <div style={{ fontSize: 9.5, color: '#665518', marginTop: 2 }}>
                          not ready — {it.status === 'PO Issued'
                            ? 'waiting on the vendor’s order confirmation'
                            : 'PO not yet issued'}
                        </div>
                      )}
                    </td>
                    <td style={Object.assign({}, tdBase, { whiteSpace: 'nowrap', fontFeatureSettings: '"tnum", "lnum"' })}>
                      {vendorState === 'none' ? (
                        <span style={{ color: '#888880' }}>—</span>
                      ) : (
                        <span>
                          <span style={{ color: '#111111' }}>{fmt(vendorPaid)}</span>
                          <span style={{ color: '#888880' }}> of {fmt(vendorNet)}</span>
                        </span>
                      )}
                      <div style={{ fontSize: 9.5, marginTop: 2, color: vendorState === 'paid' ? '#1A5C38' : (vendorState === 'partial' ? '#D4007A' : '#888880') }}>
                        {vendorState === 'paid' ? 'paid ✓' : (vendorState === 'partial' ? 'vendor balance open' : 'nothing recorded')}
                      </div>
                    </td>
                    <td style={moneyCell(depositPaid, { green: true })}>{fmt(depositPaid)}</td>
                    <td style={moneyCell(finalDueBase)}>{fmt(finalDueBase)}</td>
                    <td style={moneyCell(feesRounded)}>{fmt(feesRounded)}</td>
                    <td style={moneyCell(est)}>{fmt(est)}</td>
                  </tr>
                );
            })}
          </React.Fragment>
        );
      })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ── DETAIL MODAL ──────────────────────────────────────────────────────────────

function InvoiceDetail(p) {
  var inv = p.invoice;
  // Every numbering system this invoice touches — SKUs, POs, proposal,
  // billing-summary month, sibling DEP/FIN refs (2026-07-19, Dan's QB
  // reconciliation). Shared derivation with the list + the PDF block.
  var crossRefs = invoiceCrossRefs(inv, {
    items: p.items || [], proposals: p.proposals || [],
    purchaseOrders: p.purchaseOrders || [], invoices: p.invoices || [],
    billingSummaries: p.billingSummaries || [],
  });
  var pdfs = useState({ url: null, loading: false, err: '' });
  var pdf = pdfs[0]; var setPdf = pdfs[1];
  // Ref-tracked blob URL so the useEffect cleanup revokes the LATEST
  // value (the closure-captured pdf.url is always null at cleanup time
  // because the cleanup function was constructed before setPdf ran).
  var urlRef = useRef(null);
  var dcs = useState(false); var deleteConfirm = dcs[0]; var setDeleteConfirm = dcs[1];
  var rfs = useState(false); var refreshing = rfs[0]; var setRefreshing = rfs[1];

  // ── Refresh specs (2026-09-11) ────────────────────────────────────────
  // The snapshot freezes the whole line at Save & Commit, money and prose
  // alike. Correcting a lead time (or a finish, or a photo) on the item
  // afterwards used to be unreachable: the only route was deleting the
  // invoice, which does not unwind the final_invoice_id stamps it left on
  // its items. This pulls the descriptive half forward and leaves every
  // number alone — and only for an invoice that has not gone anywhere.
  //
  // The archive PDF is replaced too. It has to be: the billing package
  // serves the stored archive FIRST and only falls back to a regen, so an
  // un-replaced archive is the copy the client actually receives.
  var refreshGate = canRefreshInvoiceSnapshot(inv, p.paymentAllocations || []);

  function regenPayloadFor(invoice) {
    return buildInvoiceRegenPayload(invoice, {
      clients:            p.clients            || [],
      projects:           p.projects           || [],
      items:              p.items              || [],
      proposals:          p.proposals          || [],
      purchaseOrders:     p.purchaseOrders     || [],
      invoices:           p.invoices           || [],
      billingSummaries:   p.billingSummaries   || [],
      paymentAllocations: p.paymentAllocations || [],
      clientPayments:     p.clientPayments     || [],
    });
  }

  async function refreshSpecs() {
    if (refreshing) return;
    var gate = canRefreshInvoiceSnapshot(inv, p.paymentAllocations || []);
    if (!gate.ok) { window.alert('Cannot refresh specs:\n\n' + gate.reason); return; }

    var res = refreshInvoiceSnapshotSpecs(inv, p.items || []);
    if (!res.ok) { window.alert('Cannot refresh specs:\n\n' + res.error); return; }
    if (res.edits.length === 0) {
      window.alert('The specs on this invoice already match the items. Nothing to refresh.');
      return;
    }

    var shown = res.edits.slice(0, 20).map(function(e) {
      return '• ' + (e.sku ? e.sku + '  ' : '') + e.label + ': "' + e.from + '" → "' + e.to + '"';
    });
    if (res.edits.length > 20) shown.push('…and ' + (res.edits.length - 20) + ' more');
    var verb = res.edits.length === 1 ? 'this change' : 'these ' + res.edits.length + ' changes';
    if (!window.confirm('Pull ' + verb + ' onto ' + ref + '?\n\n' + shown.join('\n') +
        '\n\nPrices, quantities and totals are not touched.')) return;

    setRefreshing(true);
    setPdf({ url: null, loading: true, err: '' });
    try {
      var nextInv = Object.assign({}, inv, { lineItems: res.lines });
      var fetched = await fetch('/api/generate-deposit-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regenPayloadFor(nextInv)),
      });
      if (!fetched.ok) {
        var errBody = await fetched.json().catch(function() { return {}; });
        throw new Error(errBody.error || 'PDF render failed (HTTP ' + fetched.status + ')');
      }
      var blob = await fetched.blob();

      // Row first — the snapshot is the record; the archive is derived from
      // it. If the archive replacement fails after this, the invoice is
      // still correct everywhere the snapshot is read.
      if (p.onSnapshotRefreshed) p.onSnapshotRefreshed(res.lines);

      var archivePath = inv.pdfStoragePath || inv.pdf_storage_path;
      if (archivePath) {
        var written = await uploadFinPdfArchive(inv.id, blob, archivePath);
        if (!written) {
          window.alert('Specs updated, but the archived PDF ("View Original") could not be '
            + 'replaced and still shows the old specs. Check the console for the storage error.');
        }
      }

      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      var u = URL.createObjectURL(blob);
      urlRef.current = u;
      setPdf({ url: u, loading: false, err: '' });
    } catch (e) {
      setPdf({ url: null, loading: false, err: e.message || 'Refresh failed' });
      window.alert('Refresh failed: ' + (e.message || 'unknown error') +
        '\n\nNothing was changed.');
    } finally {
      setRefreshing(false);
    }
  }

  // Try to render the PDF on open. If we have a line_items snapshot we can
  // call the same PDF endpoint that originally generated the invoice. Legacy
  // rows (from BillingTab) without a snapshot get a hint banner instead.
  //
  // Route by invoice type:
  //   reimbursable    -> /api/generate-reimbursable-invoice (different payload)
  //   deposit / final -> /api/generate-deposit-invoice
  //   designer_hours  -> handled separately under DesignerHoursSection
  React.useEffect(function() {
    var snapshot = inv.lineItems || inv.line_items || [];
    if (!snapshot || snapshot.length === 0) {
      // No snapshot — nothing to render. Don't fetch.
      setPdf({ url: null, loading: false, err: '' });
      return;
    }

    setPdf({ url: null, loading: true, err: '' });

    var invoiceType = inv.invoiceType || 'deposit';
    var endpoint, payload, parseAsBlob = true;

    if (invoiceType === 'reimbursable') {
      // Look up client for billing address + project for display name
      var client = (p.clients || []).find(function(c) { return c.id === (inv.clientId || inv.client_id); });
      var projLabel = inv.project || inv.project_label || '';
      var projectRec = (p.projects || []).find(function(pr) { return pr.label === projLabel; });
      var projectDisplay = (projectRec && projectRec.displayName) || projLabel;
      var createdAtStr = String(inv.createdAt || inv.created_at || new Date().toISOString());
      var d = new Date(createdAtStr);
      var period = isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      var invoiceDate = isNaN(d.getTime())
        ? createdAtStr.slice(0, 10)
        : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      endpoint = '/api/generate-reimbursable-invoice';
      payload = {
        invoiceRef:     inv.invoiceRef || inv.invoice_ref || '',
        invoiceDate:    invoiceDate,
        projectDisplay: projectDisplay,
        billTo: {
          name:    inv.clientName || inv.client_name || (client ? client.name : ''),
          address: client ? (client.billingAddress || '') : '',
          city:    client ? (client.billingCity    || '') : '',
          state:   client ? (client.billingState   || '') : '',
          zip:     client ? (client.billingZip     || '') : '',
        },
        period:    period,
        lineItems: snapshot,
        subtotal:  parseFloat(inv.amountDue) || parseFloat(inv.amount) || 0,
        notes:     inv.notes || '',
        markupPct: 15,
      };
      parseAsBlob = false; // reimbursable endpoint returns { pdf: base64, filename }
    } else {
      // ONE builder for every regenerate-from-snapshot surface. Until
      // 2026-09-11 this preview inlined its own payload and
      // buildInvoiceRegenPayload (the billing-package zip fallback) built
      // a second, different one — so the same invoice regenerated three
      // ways produced three documents, none of which matched the archive.
      // Everything the builder needs to reproduce the issued PDF is passed
      // here; the builder holds the field-by-field reasoning.
      endpoint = '/api/generate-deposit-invoice';
      payload = regenPayloadFor(inv);
    }

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(d) { throw new Error(d.error || 'PDF failed'); });
        return parseAsBlob ? res.blob() : res.json();
      })
      .then(function(result) {
        var blob;
        if (parseAsBlob) {
          blob = result;
        } else {
          // reimbursable endpoint shape: { pdf: base64, filename }
          var bytes = Uint8Array.from(atob(result.pdf), function(c) { return c.charCodeAt(0); });
          blob = new Blob([bytes], { type: 'application/pdf' });
        }
        var u = URL.createObjectURL(blob);
        urlRef.current = u;
        setPdf({ url: u, loading: false, err: '' });
      })
      .catch(function(e) {
        setPdf({ url: null, loading: false, err: e.message || 'PDF render failed' });
      });

    return function() {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [inv.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function downloadPdf() {
    if (!pdf.url) return;
    var a = document.createElement('a');
    a.href = pdf.url;
    a.download = (inv.invoiceRef || inv.invoice_ref || 'invoice').replace(/[^a-zA-Z0-9-]/g, '') + '.pdf';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }

  var ref     = inv.invoiceRef    || inv.invoice_ref    || inv.invoice_number || '—';
  var type    = inv.invoiceType   || 'final';
  var project = inv.project       || inv.project_label  || '—';
  var clientName = inv.clientName || inv.client_name    || '';
  var amount  = inv.amountDue     || inv.amount         || 0;
  var status  = inv.status        || 'draft';
  var date    = inv.createdAt     || inv.created_at     || '';
  var sentAt  = inv.sentAt        || '';
  var hasSnapshot = (inv.lineItems || inv.line_items || []).length > 0;
  var isPaid = status === 'paid';

  return (
    <>
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '92vw', maxWidth: 1080, height: '92vh', background: '#fff', borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>

        {/* Toolbar */}
        <div style={S.toolbar}>
          <span style={Object.assign({}, S.pageTitle, { flex: 1, fontFamily: 'monospace' })}>{ref} · {fmtMoney(amount)}</span>
          <StatusPill status={status} />
          <div style={S.vsep} />

          {pdf.url && (
            <button style={S.tbtnGhost} onClick={downloadPdf}>↓ Download</button>
          )}
          {/* Refresh specs (2026-09-11). Only on an invoice that has not
              left the office — canRefreshInvoiceSnapshot decides, and its
              reason becomes the disabled button's tooltip so the rule is
              legible instead of the button simply not being there. */}
          {p.canManage && hasSnapshot && (
            <button
              style={Object.assign({}, S.tbtnGhost, refreshGate.ok
                ? null
                : { opacity: 0.4, cursor: 'default' })}
              disabled={!refreshGate.ok || refreshing}
              title={refreshGate.ok
                ? 'Pull the item specs — product, description, dimensions, finish, lead time, sidemark, photos — forward from the items. Prices and totals are not touched.'
                : refreshGate.reason}
              onClick={refreshSpecs}>
              {refreshing ? 'Refreshing…' : '↻ Refresh specs'}
            </button>
          )}
          {/* View Original (2026-06-07 Bundle 3). When a FIN has a
              pdf_storage_path, this button fetches a signed URL for
              the archived PDF blob — the exact document delivered to
              the client at Save & Commit time. Distinct from the
              live-regenerated PDF shown in the preview iframe (which
              can drift if inputs like depositPaid or items.fees
              change post-issuance). */}
          {(inv.pdfStoragePath || inv.pdf_storage_path) && (
            <button style={S.tbtnGhost}
              title="Open the exact PDF saved at finalize. The live preview can drift if inputs change after the FIN was committed."
              onClick={async function() {
                var path = inv.pdfStoragePath || inv.pdf_storage_path;
                var res = await supabase.storage.from('invoices').createSignedUrl(path, 60 * 10);
                if (res && res.data && res.data.signedUrl) {
                  window.open(res.data.signedUrl, '_blank');
                } else {
                  window.alert('Could not fetch the archived PDF. The file may have been deleted, or storage access is restricted. Technical detail: ' + (res && res.error && res.error.message || 'unknown'));
                }
              }}>
              ↗ View Original
            </button>
          )}

          {/* Lifecycle buttons.
              2026-06-07: Mark Ready added between draft and sent.
              Ready = finalized for inclusion in the next billing
              summary; the summary's Mark Sent cascade flips ready
              FINs to sent (the canonical delivery event). Mark Sent
              directly is retained for off-cycle delivery (the same
              dual-path model as proposals' sentVia field). */}
          {p.canManage && status === 'draft' && p.onMarkReady && (
            <button style={S.tbtnGhost} onClick={p.onMarkReady}
              title="Finalize this FIN for the next billing summary. The summary's Mark Sent will deliver it to the client.">
              Mark Ready
            </button>
          )}
          {p.canManage && (status === 'draft' || status === 'ready') && (
            <button style={S.tbtnGhost} onClick={p.onMarkSent}
              title="Send directly (off-cycle) without bundling into a billing summary. Use Mark Ready for on-cycle delivery.">
              Mark Sent
            </button>
          )}
          {p.canManage && !isPaid && status !== 'draft' && (
            <button style={S.tbtnSuccess} onClick={p.onMarkPaid}>Mark Paid</button>
          )}
          {p.canManage && isPaid && (
            <button style={S.tbtnGhost} onClick={p.onUnpay}>Unmark paid</button>
          )}
          {p.canManage && (
            !deleteConfirm
              ? <button style={S.tbtnDanger} onClick={function() { setDeleteConfirm(true); }}>Delete</button>
              : <>
                  <span style={{ fontSize: 11, color: '#C8322A', whiteSpace: 'nowrap' }}>Delete?</span>
                  <button style={S.tbtnDanger} onClick={p.onDelete}>Confirm</button>
                  <button style={S.tbtnGhost} onClick={function() { setDeleteConfirm(false); }}>Cancel</button>
                </>
          )}
          <button style={S.tbtnGhost} onClick={p.onClose}>Close</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

          {/* PDF preview */}
          <div style={{ flex: 1, borderRight: '0.5px solid #D8D7D2', background: '#F7F7F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {pdf.loading && <div style={{ color: '#888880', fontSize: 12 }}>Rendering PDF…</div>}
            {pdf.err && <div style={{ color: '#C8322A', fontSize: 12, padding: 24, textAlign: 'center' }}>PDF render failed: {pdf.err}</div>}
            {pdf.url && <iframe src={pdf.url} style={{ flex: 1, border: 'none', width: '100%', height: '100%' }} title={ref} />}
            {!pdf.loading && !pdf.url && !pdf.err && !hasSnapshot && (
              <div style={{ padding: 32, textAlign: 'center', color: '#888880', fontSize: 12, lineHeight: 1.6, maxWidth: 360 }}>
                <div style={{ fontSize: 32, color: '#D8D7D2', marginBottom: 12 }}>▭</div>
                <div style={{ color: '#111111', fontWeight: 500, fontSize: 14, marginBottom: 6 }}>
                  No PDF on file
                </div>
                <div>
                  This invoice was created before line-item snapshots were stored. Re-generate it from
                  the source (Proposal for deposits, Billing tab for finals) to create a snapshot
                  that can be re-rendered any time.
                </div>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div style={{ width: 360, padding: 18, overflowY: 'auto', background: '#FFFFFF' }}>
            <div style={{ marginBottom: 14 }}>
              <Field label="Invoice Type"   value={TYPE_LABEL[type] || type} />
              <Field label="Project"        value={project} />
              <Field label="Bill To"        value={clientName || '—'} />
              <Field label="Created"        value={fmtDate(date)} />
              {sentAt && <Field label="Sent" value={fmtDate(sentAt)} />}
            </div>

            {/* Cross-references — every numbering system this invoice
                touches, for QB reconciliation (2026-07-19). */}
            <div data-testid="invoice-crossrefs" style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 2 }}>Cross-references</div>
              {crossRefs.skus.length > 0 && <Field label="SKU" value={crossRefs.skus.join(' · ')} mono />}
              {crossRefs.poRefs.length > 0 && <Field label="PO #" value={crossRefs.poRefs.join(' · ')} mono />}
              {crossRefs.proposalRef && <Field label="Proposal" value={crossRefs.proposalRef} mono />}
              {crossRefs.summaryMonthYear && <Field label="Billing Month" value={crossRefs.summaryMonthYear} />}
              {crossRefs.depRefs.length > 0 && <Field label="Deposit Inv" value={crossRefs.depRefs.join(' · ')} mono />}
              {crossRefs.finRefs.length > 0 && <Field label="Final Inv" value={crossRefs.finRefs.join(' · ')} mono />}
              {crossRefs.skus.length === 0 && crossRefs.poRefs.length === 0 && !crossRefs.proposalRef
                && !crossRefs.summaryMonthYear && crossRefs.depRefs.length === 0 && crossRefs.finRefs.length === 0 && (
                <div style={{ fontSize: 10, color: '#BBBBBB', padding: '6px 0' }}>None yet — links appear as items, POs, and summaries attach.</div>
              )}
            </div>

            {/* Client deposit payments (2026-09-03, Cimon: "show initial
                client payments from proposals and the date the payment
                was made"). FINs only; derived live from the DEP
                allocations (Rule 10) — date · DEP ref · amount per
                payment. Money label names its side. */}
            {(inv.invoiceType || 'final') === 'final' && (function() {
              var depHist = finDepositPaymentHistory(
                inv, p.items || [], p.invoices || [],
                p.paymentAllocations || [], p.clientPayments || []
              );
              if (depHist.length === 0) return null;
              var depTot = Math.round(depHist.reduce(function(s, r) { return s + r.amount; }, 0) * 100) / 100;
              return (
                <div data-testid="fin-deposit-history" style={{ marginBottom: 14, padding: '10px 12px', background: '#F7F7F5', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 6 }}>Client deposit payments</div>
                  {depHist.map(function(r, i) {
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontSize: 11, padding: '3px 0', borderBottom: i < depHist.length - 1 ? '0.5px solid #ECEBE6' : 'none' }}>
                        <span style={{ color: '#555552', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}{r.method ? ' · ' + r.method : ''}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#888880', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.depRef}</span>
                        <span style={{ color: '#1A5C38', fontWeight: 500, whiteSpace: 'nowrap' }}>{fmtMoney(r.amount)}</span>
                      </div>
                    );
                  })}
                  {depHist.length > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 600, paddingTop: 6, marginTop: 3, borderTop: '0.5px solid #D8D7D2' }}>
                      <span>Total deposit paid</span>
                      <span style={{ color: '#1A5C38' }}>{fmtMoney(depTot)}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            <PaymentsPanel
              amountDue={amount}
              entries={p.entries || []}
              canManage={p.canManage}
              userEmail={p.userEmail}
              documentContext={{
                documentType: 'invoice',
                documentId:   inv.id,
                clientId:     inv.clientId || inv.client_id || '',
                projectLabel: inv.project  || inv.project_label || '',
              }}
              onRecordPayment={p.onRecordPayment}
              onUpdatePayment={p.onUpdatePayment}
              onRemoveAllocation={p.onRemoveAllocation} />

            {inv.proposalId && (function() {
              var srcProp = (p.proposals || []).find(function(pp) { return pp.id === inv.proposalId; });
              var label = srcProp && srcProp.proposal_ref ? srcProp.proposal_ref : inv.proposalId;
              return (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#F7F7F5', borderRadius: 4, fontSize: 11, color: '#555552' }}>
                  <strong style={{ color: '#111111' }}>Source: Proposal</strong>{' '}
                  <span style={{ color: '#111', fontWeight: 600 }}>{label}</span>
                  {srcProp && srcProp.proposal_name ? <span style={{ color: '#888' }}> · {srcProp.proposal_name}</span> : null}
                  {/* Proposal totals (2026-09-03) — the client pays ON
                      proposals, so these are the bookkeeper's anchor
                      figures when reconciling this FIN. */}
                  {srcProp && (parseFloat(srcProp.total_client != null ? srcProp.total_client : srcProp.totalClient) || 0) > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span>Proposal total <strong style={{ color: '#111' }}>{fmtMoney(parseFloat(srcProp.total_client != null ? srcProp.total_client : srcProp.totalClient) || 0)}</strong></span>
                      {(parseFloat(srcProp.total_deposit != null ? srcProp.total_deposit : srcProp.totalDeposit) || 0) > 0 && (
                        <span>Deposit asked <strong style={{ color: '#111' }}>{fmtMoney(parseFloat(srcProp.total_deposit != null ? srcProp.total_deposit : srcProp.totalDeposit) || 0)}</strong></span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {inv.notes && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 11, color: '#555552', lineHeight: 1.5 }}>{inv.notes}</div>
              </div>
            )}

            {!p.canManage && (
              <div style={{ marginTop: 14, padding: '10px 12px', background: '#F7F7F5', borderRadius: 4, fontSize: 10, color: '#888880' }}>
                Read-only. Only the owner and bookkeeper can mark sent / paid or delete invoices.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function Field(p) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, padding: '6px 0', borderBottom: '0.5px solid #EFEFEC' }}>
      <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', flexShrink: 0 }}>{p.label}</span>
      <span style={{ fontSize: p.bold ? 13 : (p.mono ? 10 : 11), fontWeight: p.bold ? 500 : 400, color: '#111111', fontFamily: p.mono ? 'monospace' : 'inherit', textAlign: 'right', wordBreak: 'break-word' }}>{p.value}</span>
    </div>
  );
}
